import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { InlineMath, BlockMath } from 'react-katex';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  collection, onSnapshot, query, orderBy,
  doc, setDoc, updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import ModuleExamPlayer from '../components/student/ModuleExamPlayer';
import { useAuth } from '../context/AuthContext';
import {
  generateLectureContentStream,
  generateChatResponseStream,
  scoreExtendedResponse,
  SUBJECT_NAMES,
} from '../utils/gemini';
import { SBA, SUBJECTS_THEME } from '../theme';
import AppShell from '../components/layout/AppShell';
import SafeImage from '../components/SafeImage';
import {
  normalizeSolvedImages,
  combineSolvedImageDescriptions,
} from '../utils/lectureStorage';
import useStudySessionTracker from '../hooks/useStudySessionTracker';
import StudyFocusPopup from '../components/student/StudyFocusPopup';
import LectureTtsControls from '../components/student/LectureTtsControls';
import { getAppNavItems } from '../utils/appNavItems';
import { hasAllExamsUnlocked } from '../utils/examAccess';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import useChatMessageTts from '../hooks/useChatMessageTts';
import { lectureContentToSpeechText } from '../utils/lectureTts';
import { loadLectureCache, saveLectureContent, saveLectureChat, clearLectureMemoryCache } from '../utils/studentLectureCache';
import { hasAuthoredLecture, lectureImagesExplainText, foldLectureBlocks, sectionImages, serializeLectureBlocks } from '../utils/lectureContent';
import { toQuizModalQuestions as toAuthoredQuizQuestions, drawLectureQuizQuestions, drawModuleExamQuestions, lectureQuizQuestionKeys, LECTURE_QUIZ_DRAW, MODULE_EXAM_DRAW, optionText, optionImageUrl } from '../utils/authoredQuiz';
import { normalizeYoutubeVideos, extractYoutubeId, toYoutubeEmbedUrl } from '../utils/youtube';
import {
  lectureQuizId,
  moduleQuizId,
  loadQuiz,
  saveQuizQuestions,
  recordQuizAttempt,
  recordExtendedResponseAttempt,
  markQuizCleared,
  MAX_QUIZ_TRIES_BEFORE_SKIP,
} from '../utils/studentQuizzes';
import {
  ER_SUBJECT,
  countWords,
  erQuizId,
  firstPendingExtendedResponse,
  isErItemLocked,
  isRlaExtendedUnlocked,
  normalizeErScore,
} from '../utils/extendedResponses';
import ExtendedResponseWriter from '../components/student/ExtendedResponseWriter';
import ImageMapReadingViewer from '../components/student/ImageMapReadingViewer';
import {
  IMR_SUBJECT,
  firstPendingImageMapReading,
  imrQuizId,
  isImrItemLocked,
  isSocialStudiesImrUnlocked,
  toQuizModalQuestions,
} from '../utils/imageMapReadings';

const SUBJECTS = SUBJECTS_THEME;

function lastLessonStorageKey(uid) {
  return `ged:lastLesson:${uid}`;
}

function readLocalLastLesson(uid) {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(lastLessonStorageKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocalLastLesson(uid, payload) {
  if (!uid || !payload) return;
  try {
    localStorage.setItem(lastLessonStorageKey(uid), JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export default function CoursePage() {
  const { currentUser, isAdmin, hasSubscription } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [modules, setModules] = useState([]);
  const [daysByModule, setDaysByModule] = useState({});
  const [completedLessons, setCompletedLessons] = useState({});
  const [lastPosition, setLastPosition] = useState(null);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [activeSubject, setActiveSubject] = useState('math');
  const [expandedModuleId, setExpandedModuleId] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loadingModules, setLoadingModules] = useState(true);
  const [marking, setMarking] = useState(false);
  const hasRestoredRef = useRef(false);

  // AI lecture content
  const [aiContent, setAiContent] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [regenerateKey, setRegenerateKey] = useState(0);
  const skipLectureCacheDayRef = useRef(null);
  const [aiStreaming, setAiStreaming] = useState(false);

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatStreamContent, setChatStreamContent] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatStreaming, setChatStreaming] = useState(false);

  // Quiz
  const [quizState, setQuizState] = useState(null); // null | 'loading' | 'active' | 'submitted'
  const [quizData, setQuizData] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [pendingNextLesson, setPendingNextLesson] = useState(null);
  const [passedLectureQuizzes, setPassedLectureQuizzes] = useState({});
  const [passedModuleQuizzes, setPassedModuleQuizzes] = useState({});
  const [clearedLectureQuizzes, setClearedLectureQuizzes] = useState({});
  const [clearedModuleQuizzes, setClearedModuleQuizzes] = useState({});
  const [quizKind, setQuizKind] = useState('lecture'); // lecture | module
  const [quizContext, setQuizContext] = useState(null); // { module, day?, title, questionCount }
  const [quizAttemptsUsed, setQuizAttemptsUsed] = useState(0);
  const [quizPracticeOnly, setQuizPracticeOnly] = useState(false);
  const [examPrompt, setExamPrompt] = useState(null);
  const [extendedResponses, setExtendedResponses] = useState([]);
  const [selectedEr, setSelectedEr] = useState(null);
  const [passedExtendedResponses, setPassedExtendedResponses] = useState({});
  const [clearedExtendedResponses, setClearedExtendedResponses] = useState({});
  const [erResult, setErResult] = useState(null);
  const [erSubmitting, setErSubmitting] = useState(false);
  const [erAttemptsUsed, setErAttemptsUsed] = useState(0);
  const [erPracticeOnly, setErPracticeOnly] = useState(false);
  const [erPrompt, setErPrompt] = useState(null);
  const [imageMapReadings, setImageMapReadings] = useState([]);
  const [selectedImr, setSelectedImr] = useState(null);
  const [passedImageMapReadings, setPassedImageMapReadings] = useState({});
  const [clearedImageMapReadings, setClearedImageMapReadings] = useState({});
  const [imrPrompt, setImrPrompt] = useState(null);

  const trackingSubject = selectedDay?.module?.subject || (selectedImr ? IMR_SUBJECT : '');
  const { showFocusPopup, dismissFocusPopup } = useStudySessionTracker(currentUser?.uid, trackingSubject);

  // Fetch modules + days
  useEffect(() => {
    const dayUnsubs = {};
    const modulesUnsub = onSnapshot(
      query(collection(db, 'modules'), orderBy('order', 'asc')),
      (snap) => {
        const mods = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setModules(mods);
        setLoadingModules(false);
        const firstSubject = SUBJECTS.find((sub) => mods.some((m) => m.subject === sub.id));
        if (firstSubject) {
          setActiveSubject((prev) => (mods.some((m) => m.subject === prev) ? prev : firstSubject.id));
        }
        const firstMod = mods.find((m) => m.subject === (firstSubject?.id || 'math'));
        if (firstMod) {
          setExpandedModuleId((prev) => prev || firstMod.id);
        }
        mods.forEach((mod) => {
          if (!dayUnsubs[mod.id]) {
            dayUnsubs[mod.id] = onSnapshot(
              query(collection(db, 'modules', mod.id, 'days'), orderBy('order', 'asc')),
              (dSnap) => {
                const days = dSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setDaysByModule((prev) => ({ ...prev, [mod.id]: days }));
              }
            );
          }
        });
      }
    );
    return () => { modulesUnsub(); Object.values(dayUnsubs).forEach((u) => u()); };
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'extendedResponses'),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((row) => !row.subject || row.subject === ER_SUBJECT)
          .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setExtendedResponses(rows);
      },
      () => setExtendedResponses([])
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'imageMapReadings'),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((row) => !row.subject || row.subject === IMR_SUBJECT)
          .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setImageMapReadings(rows);
      },
      () => setImageMapReadings([])
    );
    return () => unsub();
  }, []);

  // Fetch progress + last lecture position
  useEffect(() => {
    if (!currentUser) return;
    setProgressLoaded(false);
    hasRestoredRef.current = false;
    const ref = doc(db, 'userProgress', currentUser.uid);
    return onSnapshot(ref, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setCompletedLessons(data.completedLessons || {});
      setLastPosition(data.lastPosition || null);
      setPassedLectureQuizzes(data.passedLectureQuizzes || {});
      setPassedModuleQuizzes(data.passedModuleQuizzes || {});
      setClearedLectureQuizzes(data.clearedLectureQuizzes || {});
      setClearedModuleQuizzes(data.clearedModuleQuizzes || {});
      setPassedExtendedResponses(data.passedExtendedResponses || {});
      setClearedExtendedResponses(data.clearedExtendedResponses || {});
      setPassedImageMapReadings(data.passedImageMapReadings || {});
      setClearedImageMapReadings(data.clearedImageMapReadings || {});
      setProgressLoaded(true);
    });
  }, [currentUser]);

  const persistLastPosition = useCallback(
    async (module, day) => {
      if (!currentUser || !module || !day) return;
      const payload = {
        moduleId: module.id,
        dayId: day.id,
        subject: module.subject || '',
        updatedAt: Date.now(),
      };
      writeLocalLastLesson(currentUser.uid, payload);
      setLastPosition(payload);
      try {
        await setDoc(doc(db, 'userProgress', currentUser.uid), { lastPosition: payload }, { merge: true });
      } catch {
        /* offline / rules — local copy still helps */
      }
    },
    [currentUser]
  );

  // Lecture body: admin-authored content first; otherwise AI generate + cache
  useEffect(() => {
    if (!selectedDay) return;
    const day = selectedDay.day;
    if (hasAuthoredLecture(day)) {
      const authored =
        String(day.content || '').trim() || serializeLectureBlocks(day.contentBlocks || []);
      setAiContent(authored);
      setAiLoading(false);
      setAiError(null);
      setAiStreaming(false);
      return;
    }
    if (!day.topic) {
      setAiContent(day.content || '');
      setAiLoading(false);
      setAiError(null);
      setAiStreaming(false);
      return;
    }

    const moduleDays = daysByModule[selectedDay.module.id] || [];
    const isRevisionLecture = Boolean(day.isRevisionLecture);
    const priorLectures = isRevisionLecture
      ? moduleDays
          .filter((d) => d.id !== day.id)
          .map((d) => ({
            title: d.title,
            topic: d.topic || d.title,
          }))
      : [];

    setAiError(null);
    setAiLoading(true);
    setAiStreaming(false);
    let cancelled = false;
    (async () => {
      try {
        const skipCache = skipLectureCacheDayRef.current === day.id;
        if (skipCache) skipLectureCacheDayRef.current = null;
        if (currentUser?.uid && !skipCache) {
          const cached = await loadLectureCache(currentUser.uid, day.id);
          if (cancelled) return;
          if (cached?.content) {
            setAiContent(cached.content);
            setChatMessages(cached.chatMessages || []);
            setAiLoading(false);
            setAiStreaming(false);
            return;
          }
        }

        setAiContent('');
        setChatMessages([]);
        setAiLoading(false);
        setAiStreaming(true);
        let fullContent = '';
        await generateLectureContentStream(
          {
            topic: day.topic,
            subject: selectedDay.module.subject,
            moduleTitle: selectedDay.module.title,
            lectureTitle: day.title,
            solvedImageDescription: [
              combineSolvedImageDescriptions(day),
              lectureImagesExplainText(day),
            ]
              .filter(Boolean)
              .join('\n\n'),
            mode: isRevisionLecture ? 'revision' : 'lecture',
            priorLectures,
          },
          (chunk) => {
            if (cancelled) return;
            fullContent += chunk;
            setAiContent((prev) => (prev ?? '') + chunk);
          }
        );
        if (cancelled) return;
        setAiStreaming(false);
        if (currentUser?.uid && fullContent) {
          await saveLectureContent(currentUser.uid, day.id, fullContent, {
            moduleId: selectedDay.module.id,
            subject: selectedDay.module.subject,
            title: day.title,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setAiError(err.message || 'Failed to generate content.');
          setAiLoading(false);
          setAiStreaming(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDay, regenerateKey, daysByModule, currentUser]);

  useEffect(() => {
    setChatInput('');
    setChatStreamContent('');
    setChatStreaming(false);
    setChatLoading(false);
  }, [selectedDay?.day?.id]);

  function handleRegenerate() {
    if (!selectedDay) return;
    if (currentUser?.uid) {
      clearLectureMemoryCache(currentUser.uid, selectedDay.day.id);
    }
    skipLectureCacheDayRef.current = selectedDay.day.id;
    setAiStreaming(false);
    setChatMessages([]);
    setRegenerateKey((k) => k + 1);
  }

  function isLectureQuizPassed(dayId) {
    return Boolean(passedLectureQuizzes[dayId]);
  }

  function isLectureQuizCleared(dayId) {
    return Boolean(passedLectureQuizzes[dayId] || clearedLectureQuizzes[dayId]);
  }

  function isModuleQuizPassed(moduleId) {
    return Boolean(passedModuleQuizzes[moduleId]);
  }

  function isModuleQuizCleared(moduleId) {
    return Boolean(passedModuleQuizzes[moduleId] || clearedModuleQuizzes[moduleId]);
  }

  function isErCleared(erId) {
    return Boolean(passedExtendedResponses[erId] || clearedExtendedResponses[erId]);
  }

  function isImrCleared(imrId) {
    return Boolean(passedImageMapReadings[imrId] || clearedImageMapReadings[imrId]);
  }

  const examsOpen = hasAllExamsUnlocked(currentUser?.email);
  const rlaErUnlocked = examsOpen || isRlaExtendedUnlocked(modules, daysByModule, completedLessons);
  const ssImrUnlocked = examsOpen || isSocialStudiesImrUnlocked(modules, daysByModule, completedLessons);

  function maybePromptFirstEr(afterNext = null) {
    if (!rlaErUnlocked || extendedResponses.length === 0) return false;
    const pending = firstPendingExtendedResponse(
      extendedResponses,
      passedExtendedResponses,
      clearedExtendedResponses
    );
    if (!pending) return false;
    setErPrompt({ item: pending, afterNext });
    return true;
  }

  function maybePromptFirstImr(afterNext = null) {
    if (!ssImrUnlocked || imageMapReadings.length === 0) return false;
    const pending = firstPendingImageMapReading(
      imageMapReadings,
      passedImageMapReadings,
      clearedImageMapReadings
    );
    if (!pending) return false;
    setImrPrompt({ item: pending, afterNext });
    return true;
  }

  // Next lecture unlocks after the previous lecture quiz is passed, or after two failed tries.
  const isLessonLocked = useCallback(
    (dayIdx, modIdx, subjectModules) => {
      if (modIdx === 0 && dayIdx === 0) return false;
      if (dayIdx > 0) {
        const prevDay = (daysByModule[subjectModules[modIdx].id] || [])[dayIdx - 1];
        if (!prevDay || !completedLessons[prevDay.id]) return true;
        if (prevDay.isRevisionLecture) return false;
        return !(passedLectureQuizzes[prevDay.id] || clearedLectureQuizzes[prevDay.id]);
      }
      const prevMod = subjectModules[modIdx - 1];
      if (!prevMod) return false;
      const prevDays = daysByModule[prevMod.id] || [];
      if (prevDays.length === 0) return true;
      if (!prevDays.every((d) => completedLessons[d.id])) return true;
      const lastDay = prevDays[prevDays.length - 1];
      if (lastDay && !lastDay.isRevisionLecture) {
        if (!(passedLectureQuizzes[lastDay.id] || clearedLectureQuizzes[lastDay.id])) {
          return true;
        }
      }
      return !(passedModuleQuizzes[prevMod.id] || clearedModuleQuizzes[prevMod.id]);
    },
    [
      daysByModule,
      completedLessons,
      passedLectureQuizzes,
      clearedLectureQuizzes,
      passedModuleQuizzes,
      clearedModuleQuizzes,
    ]
  );

  function isModuleExamUnlocked(mod) {
    if (hasAllExamsUnlocked(currentUser?.email)) return true;
    const days = daysByModule[mod.id] || [];
    if (days.length === 0) return false;
    const revision = days.find((d) => d.isRevisionLecture);
    if (revision) return Boolean(completedLessons[revision.id]);
    return days.every((d) => completedLessons[d.id]);
  }

  function nextLessonAfter(module, day) {
    const sequence = [];
    SUBJECTS.forEach((subject) => {
      modules
        .filter((m) => m.subject === subject.id)
        .forEach((mod) => {
          (daysByModule[mod.id] || []).forEach((d) => {
            sequence.push({ module: mod, day: d });
          });
        });
    });
    const idx = sequence.findIndex((l) => l.day.id === day.id && l.module.id === module.id);
    if (idx < 0) return null;
    const nxt = sequence[idx + 1];
    return nxt ? { module: nxt.module, day: nxt.day } : null;
  }

  function clearQuizUi() {
    setQuizState(null);
    setQuizData(null);
    setQuizAnswers({});
    setPendingNextLesson(null);
    setQuizContext(null);
    setQuizAttemptsUsed(0);
    setQuizPracticeOnly(false);
  }

  async function patchProgress(fields) {
    if (!currentUser) return;
    const ref = doc(db, 'userProgress', currentUser.uid);
    try {
      await updateDoc(ref, fields);
    } catch {
      await setDoc(ref, fields, { merge: true });
    }
  }

  async function startLectureQuiz({ module, day, nextLesson = null, forceNew = false, practiceOnly = false }) {
    const pool = toAuthoredQuizQuestions(day?.quizQuestions);
    if (!pool.length) {
      alert('This lecture quiz is not published yet. Add questions in Admin → Course Content (editor or JSON).');
      return;
    }

    const previousKeys =
      forceNew && quizContext?.day?.id === day.id ? quizContext.drawnKeys || [] : [];
    const questions = drawLectureQuizQuestions(pool, { previousKeys });

    setQuizKind('lecture');
    setQuizPracticeOnly(practiceOnly);
    setQuizContext({
      module,
      day,
      title: day.title,
      questionCount: questions.length,
      poolCount: pool.length,
      drawnKeys: lectureQuizQuestionKeys(questions),
      isRevision: Boolean(day.isRevisionLecture),
    });
    setPendingNextLesson(nextLesson);
    setQuizState('loading');
    setQuizData(null);
    setQuizAnswers({});
    try {
      const quizId = lectureQuizId(day.id);
      let priorAttempts = 0;
      if (currentUser?.uid) {
        const existing = await loadQuiz(currentUser.uid, quizId);
        priorAttempts = Number(existing?.attempts) || 0;
      }
      setQuizData(questions);
      setQuizAttemptsUsed(priorAttempts);
      setQuizState('active');
      if (currentUser?.uid) {
        await saveQuizQuestions(currentUser.uid, quizId, {
          kind: 'lecture',
          title: day.title,
          subject: module.subject,
          moduleId: module.id,
          lectureId: day.id,
          questions,
        });
      }
    } catch {
      clearQuizUi();
      alert('Could not open the lecture quiz. Please try again.');
    }
  }

  async function startModuleQuiz(module, nextLesson = null, { forceNew = false, practiceOnly = false } = {}) {
    const days = daysByModule[module.id] || [];
    const previousKeys =
      forceNew && quizContext?.module?.id === module.id ? quizContext.drawnKeys || [] : [];
    const questions = drawModuleExamQuestions({
      days,
      moduleQuestions: module?.quizQuestions,
      previousKeys,
      count: MODULE_EXAM_DRAW,
    });
    if (!questions.length) {
      alert(
        'This module exam has no questions yet. Add lecture quizzes on each lecture, and/or extra questions on Edit Module.'
      );
      return;
    }

    setQuizKind('module');
    setQuizPracticeOnly(practiceOnly);
    setQuizContext({
      module,
      day: null,
      title: module.title,
      questionCount: questions.length,
      poolCount: questions.length,
      drawnKeys: lectureQuizQuestionKeys(questions),
    });
    setPendingNextLesson(nextLesson);
    setQuizAnswers({});
    setQuizData(questions);
    setQuizAttemptsUsed(0);
    setQuizState('active');
    try {
      if (currentUser?.uid) {
        const quizId = moduleQuizId(module.id);
        const existing = await loadQuiz(currentUser.uid, quizId);
        setQuizAttemptsUsed(Number(existing?.attempts) || 0);
        await saveQuizQuestions(currentUser.uid, quizId, {
          kind: 'module',
          title: module.title,
          subject: module.subject,
          moduleId: module.id,
          questions,
        });
      }
    } catch {
      /* The exam is already on screen. A slow save must not trap the student. */
    }
  }

  async function markComplete() {
    if (!selectedDay || !currentUser) return;
    setMarking(true);
    try {
      const payload = {
        completedLessons: { [selectedDay.day.id]: true },
        lastPosition: {
          moduleId: selectedDay.module.id,
          dayId: selectedDay.day.id,
          subject: selectedDay.module.subject || '',
          updatedAt: Date.now(),
        },
      };
      writeLocalLastLesson(currentUser.uid, payload.lastPosition);
      await patchProgress({
        [`completedLessons.${selectedDay.day.id}`]: true,
        lastPosition: payload.lastPosition,
      });
      setCompletedLessons((prev) => ({ ...prev, [selectedDay.day.id]: true }));

      const nextLesson = nextLessonAfter(selectedDay.module, selectedDay.day);

      // Revision complete → no lecture quiz; ask whether to take the Module Exam now
      if (selectedDay.day.isRevisionLecture) {
        await patchProgress({ [`clearedLectureQuizzes.${selectedDay.day.id}`]: true });
        setClearedLectureQuizzes((prev) => ({ ...prev, [selectedDay.day.id]: true }));
        if (!isModuleQuizCleared(selectedDay.module.id)) {
          setExamPrompt({
            module: selectedDay.module,
            nextLesson,
            moduleTitle: selectedDay.module.title,
          });
        }
        return;
      }

      // Regular lecture → short quiz (if not already cleared)
      if (!isLectureQuizCleared(selectedDay.day.id)) {
        await startLectureQuiz({
          module: selectedDay.module,
          day: selectedDay.day,
          nextLesson,
        });
      }
    } finally {
      setMarking(false);
    }
  }

  function selectSubject(id) {
    if (id === activeSubject) return;
    setActiveSubject(id);
    setSelectedDay(null);
    setSelectedEr(null);
    setSelectedImr(null);
    const firstMod = modules.find((m) => m.subject === id);
    setExpandedModuleId(firstMod?.id || null);
  }

  function toggleModule(id) {
    setExpandedModuleId((prev) => (prev === id ? null : id));
  }

  function selectLesson(module, day, locked) {
    if (locked) return;
    setSelectedEr(null);
    setSelectedImr(null);
    setErResult(null);
    setRegenerateKey(0);
    setSelectedDay({ module, day });
    if (module.subject) {
      setActiveSubject(module.subject);
      setExpandedModuleId(module.id);
    }
    persistLastPosition(module, day);
  }

  async function openExtendedResponse(item, { practiceOnly = false } = {}) {
    if (!item) return;
    setSelectedDay(null);
    setSelectedEr(item);
    setSelectedImr(null);
    setErResult(null);
    setErPracticeOnly(practiceOnly);
    setActiveSubject(ER_SUBJECT);
    if (currentUser?.uid) {
      const existing = await loadQuiz(currentUser.uid, erQuizId(item.id));
      setErAttemptsUsed(Number(existing?.attempts) || 0);
    } else {
      setErAttemptsUsed(0);
    }
  }

  function openImageMapReading(item, { practiceOnly = false } = {}) {
    if (!item) return;
    setSelectedDay(null);
    setSelectedEr(null);
    setErResult(null);
    setSelectedImr(item);
    setActiveSubject(IMR_SUBJECT);
    if (practiceOnly) {
      /* viewer uses passed/cleared flags for practice label */
    }
  }

  async function startImageMapQuiz(item, { practiceOnly = false } = {}) {
    if (!item) return;
    const questions = toQuizModalQuestions(item.questions);
    if (!questions.length) {
      alert('This reading has no MCQs yet.');
      return;
    }
    setQuizKind('image-map');
    setQuizPracticeOnly(practiceOnly);
    setQuizContext({
      module: { id: 'image-map', subject: IMR_SUBJECT, title: item.title },
      item,
      day: null,
      title: item.title,
      questionCount: questions.length,
    });
    setPendingNextLesson(null);
    setQuizAnswers({});
    setQuizData(questions);
    setQuizState('active');
    if (currentUser?.uid) {
      const existing = await loadQuiz(currentUser.uid, imrQuizId(item.id));
      setQuizAttemptsUsed(Number(existing?.attempts) || 0);
      await saveQuizQuestions(currentUser.uid, imrQuizId(item.id), {
        kind: 'image-map',
        title: item.title,
        subject: IMR_SUBJECT,
        questions,
      });
    } else {
      setQuizAttemptsUsed(0);
    }
  }

  // All lessons in order (for prev/next)
  function getAllLessons() {
    const all = [];
    SUBJECTS.forEach((subject) => {
      modules.filter((m) => m.subject === subject.id).forEach((mod, modIdx) => {
        const subjectMods = modules.filter((m) => m.subject === subject.id);
        (daysByModule[mod.id] || []).forEach((day, dayIdx) => {
          all.push({ module: mod, day, locked: isLessonLocked(dayIdx, modIdx, subjectMods) });
        });
      });
    });
    return all;
  }

  // Resume where the student left off (login / refresh / reopen)
  useEffect(() => {
    if (hasRestoredRef.current || !currentUser || !progressLoaded || loadingModules) return;
    if (selectedDay || modules.length === 0) return;

    const stored = lastPosition || readLocalLastLesson(currentUser.uid);
    const hasAnyProgress =
      Boolean(stored?.dayId) || Object.keys(completedLessons).some((id) => completedLessons[id]);
    if (!hasAnyProgress) {
      hasRestoredRef.current = true;
      return;
    }

    if (stored?.moduleId && daysByModule[stored.moduleId] === undefined) return;

    const subjectIdsNeeded = SUBJECTS.map((s) => s.id);
    const modsForSubjects = modules.filter((m) => subjectIdsNeeded.includes(m.subject));
    const daysReady = modsForSubjects.every((m) => daysByModule[m.id] !== undefined);
    if (!daysReady) return;

    const all = [];
    SUBJECTS.forEach((subject) => {
      const subjectMods = modules.filter((m) => m.subject === subject.id);
      subjectMods.forEach((mod, modIdx) => {
        (daysByModule[mod.id] || []).forEach((day, dayIdx) => {
          all.push({
            module: mod,
            day,
            locked: isLessonLocked(dayIdx, modIdx, subjectMods),
          });
        });
      });
    });
    if (all.length === 0) return;

    let resume = null;
    if (stored?.dayId && stored?.moduleId) {
      resume = all.find(
        (l) => l.day.id === stored.dayId && l.module.id === stored.moduleId && !l.locked
      ) || null;
    }
    if (!resume) {
      resume =
        all.find((l) => !l.locked && !completedLessons[l.day.id]) ||
        [...all].reverse().find((l) => !l.locked) ||
        null;
    }
    if (!resume) {
      hasRestoredRef.current = true;
      return;
    }

    hasRestoredRef.current = true;
    setSelectedDay({ module: resume.module, day: resume.day });
    if (resume.module.subject) {
      setActiveSubject(resume.module.subject);
      setExpandedModuleId(resume.module.id);
    }
    persistLastPosition(resume.module, resume.day);
  }, [
    currentUser,
    progressLoaded,
    loadingModules,
    selectedDay,
    modules,
    daysByModule,
    lastPosition,
    completedLessons,
    isLessonLocked,
    persistLastPosition,
  ]);

  function getAdjacentLesson(dir) {
    if (!selectedDay) return null;
    const all = getAllLessons();
    const idx = all.findIndex(l => l.day.id === selectedDay.day.id && l.module.id === selectedDay.module.id);
    const target = dir === 'next' ? all[idx + 1] : all[idx - 1];
    if (!target) return null;
    if (dir === 'prev' && target.locked) return null;
    return target;
  }

  // Chat (streaming)
  async function sendChatMessage() {
    if (!chatInput.trim() || chatLoading || chatStreaming || !selectedDay) return;
    const msg = chatInput.trim();
    const historySnapshot = [...chatMessages];
    setChatMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setChatInput('');
    setChatLoading(true);
    setChatStreaming(false);
    setChatStreamContent('');
    let isFirstChunk = true;
    try {
      let fullReply = '';
      await generateChatResponseStream(
        {
          message: msg,
          lectureTitle: isRevisionLecture
            ? `${selectedDay.day.title} (Module Revision)`
            : selectedDay.day.title,
          topic: isRevisionLecture
            ? `Module revision covering: ${priorLecturesForRevision
                .map((d) => d.title)
                .join('; ')}. Focus: ${selectedDay.day.topic || selectedDay.day.title}`
            : selectedDay.day.topic || selectedDay.day.title,
          subject: selectedDay.module.subject,
          chatHistory: historySnapshot,
          lectureContent: aiContent || '',
          solvedImageDescription: [
            combineSolvedImageDescriptions(selectedDay.day),
            lectureImagesExplainText(selectedDay.day),
          ]
            .filter(Boolean)
            .join('\n\n'),
        },
        (chunk) => {
          if (isFirstChunk) {
            isFirstChunk = false;
            setChatLoading(false);
            setChatStreaming(true);
          }
          fullReply += chunk;
          setChatStreamContent(fullReply);
        }
      );
      setChatMessages((prev) => {
        const next = [
          ...prev,
          { role: 'assistant', content: fullReply || 'Sorry, I had trouble responding. Please try again.' },
        ];
        if (currentUser?.uid && selectedDay?.day?.id) {
          saveLectureChat(currentUser.uid, selectedDay.day.id, next).catch(console.warn);
        }
        return next;
      });
    } catch {
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I had trouble responding. Please try again.' },
      ]);
    } finally {
      setChatLoading(false);
      setChatStreaming(false);
      setChatStreamContent('');
    }
  }

  // Quiz
  function quizPassMark(questions, kind) {
    const total = questions?.length || 0;
    if (total === 0) return 1;
    if (kind === 'module') return Math.ceil(total * 0.7);
    if (kind === 'image-map') return Math.max(1, Math.ceil(total * 0.7));
    return Math.max(1, Math.ceil(total * 0.67));
  }

  async function handleNextLectureClick(nextLesson) {
    if (!nextLesson || !selectedDay) return;

    // Already unlocked / previously completed path
    if (completedLessons[nextLesson.day.id] && !nextLesson.locked) {
      selectLesson(nextLesson.module, nextLesson.day, false);
      return;
    }

    // Must mark lecture complete first
    if (!completedLessons[selectedDay.day.id]) {
      alert(
        selectedDay.day.isRevisionLecture
          ? 'Mark this revision as complete first. The Module Exam unlocks after that.'
          : 'Mark this lecture as complete first. A short quiz will check your understanding.'
      );
      return;
    }

    // Revision has no lecture quiz — the module exam is the gate
    if (!selectedDay.day.isRevisionLecture && !isLectureQuizCleared(selectedDay.day.id)) {
      await startLectureQuiz({
        module: selectedDay.module,
        day: selectedDay.day,
        nextLesson,
      });
      return;
    }

    if (selectedDay.day.isRevisionLecture && !isModuleQuizCleared(selectedDay.module.id)) {
      setExamPrompt({
        module: selectedDay.module,
        nextLesson,
        moduleTitle: selectedDay.module.title,
      });
      return;
    }

    // Crossing into a new module → module exam must be passed or skipped after two fails
    if (nextLesson.module.id !== selectedDay.module.id) {
      if (!isModuleQuizCleared(selectedDay.module.id)) {
        if (!isModuleExamUnlocked(selectedDay.module)) {
          alert('Complete the module revision first. The module exam unlocks after that.');
          return;
        }
        await startModuleQuiz(selectedDay.module, nextLesson);
        return;
      }
      if (
        selectedDay.module.subject === ER_SUBJECT &&
        nextLesson.module.subject !== ER_SUBJECT &&
        maybePromptFirstEr(nextLesson)
      ) {
        return;
      }
      if (
        selectedDay.module.subject === IMR_SUBJECT &&
        nextLesson.module.subject !== IMR_SUBJECT &&
        maybePromptFirstImr(nextLesson)
      ) {
        return;
      }
    }

    if (nextLesson.locked) {
      alert('This lecture is still locked. Complete the previous lecture to continue.');
      return;
    }

    selectLesson(nextLesson.module, nextLesson.day, false);
  }

  function handleQuizAnswer(qIdx, optIdx) {
    if (quizState === 'submitted') return;
    setQuizAnswers((prev) => ({ ...prev, [qIdx]: optIdx }));
  }

  async function submitQuiz() {
    if (Object.keys(quizAnswers).length < (quizData?.length || 0)) return;
    const score = quizData.filter((q, i) => quizAnswers[i] === q.correct).length;
    const need = quizPassMark(quizData, quizKind);
    const passed = score >= need;
    if (currentUser && quizContext) {
      const quizId =
        quizKind === 'module'
          ? moduleQuizId(quizContext.module.id)
          : quizKind === 'image-map'
            ? imrQuizId(quizContext.item.id)
            : lectureQuizId(quizContext.day.id);
      const record = await recordQuizAttempt(currentUser.uid, quizId, {
        score,
        total: quizData.length,
        passed,
      });
      setQuizAttemptsUsed(record?.attempts || quizAttemptsUsed + 1);
      if (passed && !quizPracticeOnly) {
        await saveQuizPass();
      }
    }
    setQuizState('submitted');
  }

  async function saveQuizPass() {
    if (!currentUser || !quizContext) return;
    if (quizKind === 'lecture' && quizContext.day) {
      await patchProgress({ [`passedLectureQuizzes.${quizContext.day.id}`]: true });
      setPassedLectureQuizzes((prev) => ({ ...prev, [quizContext.day.id]: true }));
      return;
    }
    if (quizKind === 'image-map' && quizContext.item) {
      await patchProgress({ [`passedImageMapReadings.${quizContext.item.id}`]: true });
      setPassedImageMapReadings((prev) => ({ ...prev, [quizContext.item.id]: true }));
      return;
    }
    if (quizKind === 'module' && quizContext.module) {
      await patchProgress({ [`passedModuleQuizzes.${quizContext.module.id}`]: true });
      setPassedModuleQuizzes((prev) => ({ ...prev, [quizContext.module.id]: true }));
    }
  }

  async function saveQuizClearedWithoutPass() {
    if (!currentUser || !quizContext) return;
    if (quizKind === 'lecture' && quizContext.day) {
      await patchProgress({ [`clearedLectureQuizzes.${quizContext.day.id}`]: true });
      setClearedLectureQuizzes((prev) => ({ ...prev, [quizContext.day.id]: true }));
      await markQuizCleared(currentUser.uid, lectureQuizId(quizContext.day.id));
      return;
    }
    if (quizKind === 'image-map' && quizContext.item) {
      await patchProgress({ [`clearedImageMapReadings.${quizContext.item.id}`]: true });
      setClearedImageMapReadings((prev) => ({ ...prev, [quizContext.item.id]: true }));
      await markQuizCleared(currentUser.uid, imrQuizId(quizContext.item.id));
      return;
    }
    if (quizKind === 'module' && quizContext.module) {
      await patchProgress({ [`clearedModuleQuizzes.${quizContext.module.id}`]: true });
      setClearedModuleQuizzes((prev) => ({ ...prev, [quizContext.module.id]: true }));
      await markQuizCleared(currentUser.uid, moduleQuizId(quizContext.module.id));
    }
  }

  async function proceedAfterQuizPass() {
    const score = quizData ? quizData.filter((q, i) => quizAnswers[i] === q.correct).length : 0;
    const need = quizPassMark(quizData, quizKind);
    if (score < need) return;

    if (quizPracticeOnly) {
      clearQuizUi();
      return;
    }

    await saveQuizPass();

    const next = pendingNextLesson;
    const moduleJustFinished = quizKind === 'module' ? quizContext?.module : null;
    const imrItem = quizKind === 'image-map' ? quizContext?.item : null;
    clearQuizUi();
    if (imrItem) {
      const idx = imageMapReadings.findIndex((r) => r.id === imrItem.id);
      const nxt = idx >= 0 ? imageMapReadings[idx + 1] : null;
      if (nxt && !isImrCleared(nxt.id)) {
        openImageMapReading(nxt);
      }
      return;
    }
    if (
      moduleJustFinished?.subject === ER_SUBJECT &&
      (!next || next.module.subject !== ER_SUBJECT) &&
      maybePromptFirstEr(next)
    ) {
      return;
    }
    if (
      moduleJustFinished?.subject === IMR_SUBJECT &&
      (!next || next.module.subject !== IMR_SUBJECT) &&
      maybePromptFirstImr(next)
    ) {
      return;
    }
    if (next) {
      selectLesson(next.module, next.day, false);
    }
  }

  /** After two failed tries, student may move on (module exam) or continue after lecture quiz. */
  async function continueAfterQuizFail() {
    await saveQuizClearedWithoutPass();

    const next = pendingNextLesson;
    const moduleJustFinished = quizKind === 'module' ? quizContext?.module : null;
    const imrItem = quizKind === 'image-map' ? quizContext?.item : null;
    clearQuizUi();
    if (imrItem) {
      const idx = imageMapReadings.findIndex((r) => r.id === imrItem.id);
      const nxt = idx >= 0 ? imageMapReadings[idx + 1] : null;
      if (nxt) {
        openImageMapReading(nxt);
      }
      return;
    }
    if (
      moduleJustFinished?.subject === ER_SUBJECT &&
      (!next || next.module.subject !== ER_SUBJECT) &&
      maybePromptFirstEr(next)
    ) {
      return;
    }
    if (
      moduleJustFinished?.subject === IMR_SUBJECT &&
      (!next || next.module.subject !== IMR_SUBJECT) &&
      maybePromptFirstImr(next)
    ) {
      return;
    }
    if (next) {
      selectLesson(next.module, next.day, false);
    }
  }

  function retryQuiz() {
    setQuizAnswers({});
    const ctx = quizContext;
    const next = pendingNextLesson;
    const practice = quizPracticeOnly;
    if (!ctx) return;
    if (quizKind === 'image-map') {
      startImageMapQuiz(ctx.item, { practiceOnly: practice });
      return;
    }
    if (quizKind === 'module') {
      startModuleQuiz(ctx.module, next, { forceNew: true, practiceOnly: practice });
      return;
    }
    setQuizState('loading');
    startLectureQuiz({
      module: ctx.module,
      day: ctx.day,
      nextLesson: next,
      forceNew: true,
      practiceOnly: practice,
    });
  }

  function closeQuizWithoutPass() {
    clearQuizUi();
  }

  async function submitExtendedResponse(studentText) {
    if (!selectedEr || !currentUser) return;
    setErSubmitting(true);
    try {
      const words = countWords(studentText);
      const raw = await scoreExtendedResponse({
        prompt: selectedEr.prompt,
        studentText,
        modelAnswer: selectedEr.modelAnswer,
        wordCount: words,
      });
      const scored = normalizeErScore(raw, words);
      const record = await recordExtendedResponseAttempt(currentUser.uid, erQuizId(selectedEr.id), {
        title: selectedEr.title,
        subject: ER_SUBJECT,
        extendedResponseId: selectedEr.id,
        studentText,
        ...scored,
      });
      setErAttemptsUsed(record?.attempts || erAttemptsUsed + 1);
      if (scored.passed && !erPracticeOnly) {
        await patchProgress({ [`passedExtendedResponses.${selectedEr.id}`]: true });
        setPassedExtendedResponses((prev) => ({ ...prev, [selectedEr.id]: true }));
      }
      setErResult(scored);
    } catch (err) {
      alert(err.message || 'Could not score this extended response. Please try again.');
    } finally {
      setErSubmitting(false);
    }
  }

  async function continueAfterErFail() {
    if (!selectedEr || !currentUser) return;
    await patchProgress({ [`clearedExtendedResponses.${selectedEr.id}`]: true });
    setClearedExtendedResponses((prev) => ({ ...prev, [selectedEr.id]: true }));
    await markQuizCleared(currentUser.uid, erQuizId(selectedEr.id));
    setErResult(null);
    const idx = extendedResponses.findIndex((r) => r.id === selectedEr.id);
    const nxt = extendedResponses[idx + 1];
    if (nxt) {
      openExtendedResponse(nxt);
      return;
    }
    setSelectedEr(null);
  }

  function closeErResults() {
    setErResult(null);
    if (erPracticeOnly) return;
    const idx = extendedResponses.findIndex((r) => r.id === selectedEr?.id);
    const nxt = idx >= 0 ? extendedResponses[idx + 1] : null;
    if (nxt && !isErCleared(nxt.id)) {
      openExtendedResponse(nxt);
      return;
    }
    setSelectedEr(null);
  }

  const isCurrentLessonDone = selectedDay && completedLessons[selectedDay.day.id];
  const currentModuleDays = selectedDay ? daysByModule[selectedDay.module.id] || [] : [];
  const isRevisionLecture = Boolean(selectedDay?.day?.isRevisionLecture);
  const priorLecturesForRevision = isRevisionLecture
    ? currentModuleDays.filter((d) => d.id !== selectedDay.day.id)
    : [];
  const navItems = getAppNavItems(isAdmin, location.pathname, { hasSubscription });

  function subjectProgress(subjectId) {
    const mods = modules.filter((m) => m.subject === subjectId);
    const total = mods.reduce((s, m) => s + (daysByModule[m.id]?.length || 0), 0);
    const done = mods.reduce((s, m) => s + (daysByModule[m.id] || []).filter((d) => completedLessons[d.id]).length, 0);
    return { total, done };
  }

  const activeSubjectMeta = SUBJECTS.find((sub) => sub.id === activeSubject) || SUBJECTS[0];
  const activeSubjectMods = modules.filter((m) => m.subject === activeSubject);
  const { total: subjectTotal, done: subjectDone } = subjectProgress(activeSubject);
  const subjectPct = subjectTotal > 0 ? Math.round((subjectDone / subjectTotal) * 100) : 0;

  return (
    <AppShell
      navItems={navItems}
      collapsible
      defaultCollapsed
      showBack
      backLabel="Dashboard"
      backPath="/dashboard"
    >
      <style>{`
        @keyframes gedBlink { 0%, 100% { opacity: 1 } 50% { opacity: 0 } }
        .ged-cursor {
          display: inline-block;
          width: 2px;
          height: 1em;
          background: currentColor;
          margin-left: 2px;
          vertical-align: text-bottom;
          border-radius: 1px;
          animation: gedBlink 0.75s infinite;
        }
      `}</style>

      <div className="sba-course-workspace">
        {/* One subject at a time */}
        <aside className="sba-course-rail" aria-label="Course lessons">
          <button
            type="button"
            className="sba-course-dash-back"
            onClick={() => navigate('/dashboard')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              arrow_back
            </span>
            Back to Dashboard
          </button>
          <div className="sba-course-rail-head">
            <p className="sba-course-rail-kicker">My Course</p>
            <h2 className="sba-course-rail-title">GED Prep Program</h2>
          </div>

          <label className="sba-course-subject-label" htmlFor="sba-course-subject">
            Subject
          </label>
          <select
            id="sba-course-subject"
            className="sba-course-subject-select"
            value={activeSubject}
            onChange={(e) => selectSubject(e.target.value)}
          >
            {SUBJECTS.filter((subject) => loadingModules || modules.some((m) => m.subject === subject.id)).map((subject) => {
              const prog = subjectProgress(subject.id);
              return (
                <option key={subject.id} value={subject.id}>
                  {subject.label} · {prog.done}/{prog.total}
                </option>
              );
            })}
          </select>

          <div className="sba-course-subject-card">
            <div
              className="sba-course-subject-icon"
              style={{ background: `${activeSubjectMeta.color}18`, color: activeSubjectMeta.color }}
            >
              <span className="material-symbols-outlined">{activeSubjectMeta.icon}</span>
            </div>
            <div className="sba-course-subject-copy">
              <p className="sba-course-subject-name">{activeSubjectMeta.label}</p>
              <p className="sba-course-subject-meta">
                {subjectDone}/{subjectTotal} lectures · {subjectPct}%
              </p>
            </div>
          </div>
          <div className="sba-course-progress-track" aria-hidden>
            <div
              className="sba-course-progress-fill"
              style={{ width: `${subjectPct}%`, background: activeSubjectMeta.color }}
            />
          </div>

          <div className="sba-course-lesson-list">
            {loadingModules ? (
              <p className="sba-course-muted">Loading…</p>
            ) : activeSubjectMods.length === 0 ? (
              <p className="sba-course-muted">No modules for this subject yet.</p>
            ) : (
              activeSubjectMods.map((mod, modIdx) => {
                const isModExpanded = expandedModuleId === mod.id;
                const days = daysByModule[mod.id] || [];
                const firstDayLocked = isLessonLocked(0, modIdx, activeSubjectMods);
                const modDone = days.filter((d) => completedLessons[d.id]).length;
                return (
                  <div key={mod.id} className="sba-course-mod">
                    <button
                      type="button"
                      className={`sba-course-mod-btn${firstDayLocked ? ' is-locked' : ''}${isModExpanded ? ' is-open' : ''}`}
                      onClick={() => !firstDayLocked && toggleModule(mod.id)}
                      disabled={firstDayLocked}
                    >
                      <span className="sba-course-mod-num" style={{ color: activeSubjectMeta.color, borderColor: activeSubjectMeta.color }}>
                        {firstDayLocked ? (
                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>lock</span>
                        ) : (
                          modIdx + 1
                        )}
                      </span>
                      <span className="sba-course-mod-text">
                        <span className="sba-course-mod-title">{mod.title}</span>
                        <span className="sba-course-mod-meta">
                          {firstDayLocked
                            ? 'Complete previous module first'
                            : `${modDone}/${days.length} lectures`}
                        </span>
                      </span>
                      {!firstDayLocked && (
                        <span className="material-symbols-outlined sba-course-mod-chevron">
                          expand_more
                        </span>
                      )}
                    </button>
                    {isModExpanded && !firstDayLocked && (
                      <div className="sba-course-lectures">
                        {days.map((day, dayIdx) => {
                          const locked = isLessonLocked(dayIdx, modIdx, activeSubjectMods);
                          const completed = !!completedLessons[day.id];
                          const active =
                            selectedDay?.day?.id === day.id && selectedDay?.module?.id === mod.id;
                          return (
                            <button
                              key={day.id}
                              type="button"
                              className={`sba-course-lecture${active ? ' is-active' : ''}${locked ? ' is-locked' : ''}${completed ? ' is-done' : ''}`}
                              style={active ? { borderColor: `${activeSubjectMeta.color}55`, background: `${activeSubjectMeta.color}12` } : undefined}
                              onClick={() => selectLesson(mod, day, locked)}
                              disabled={locked}
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{
                                  fontSize: 18,
                                  color: completed
                                    ? activeSubjectMeta.color
                                    : locked
                                      ? '#94a3b8'
                                      : active
                                        ? activeSubjectMeta.color
                                        : '#64748b',
                                }}
                              >
                                {completed ? 'check_circle' : locked ? 'lock' : active ? 'play_circle' : 'article'}
                              </span>
                              <span>
                                {day.isRevisionLecture
                                  ? `Revision: ${day.title}`
                                  : `Lecture ${dayIdx + 1}: ${day.title}`}
                              </span>
                            </button>
                          );
                        })}
                        {(() => {
                          const examLocked = !isModuleExamUnlocked(mod);
                          const examPassed = Boolean(passedModuleQuizzes[mod.id]);
                          const examCleared = Boolean(clearedModuleQuizzes[mod.id]);
                          return (
                            <button
                              type="button"
                              className={`sba-course-lecture${examLocked ? ' is-locked' : ''}${examPassed || examCleared ? ' is-done' : ''}`}
                              style={
                                examPassed
                                  ? { borderColor: `${activeSubjectMeta.color}55`, background: `${activeSubjectMeta.color}12` }
                                  : undefined
                              }
                              disabled={examLocked}
                              onClick={() => {
                                if (examLocked) return;
                                const revision = days.find((d) => d.isRevisionLecture) || days[days.length - 1];
                                const nextLesson = revision ? nextLessonAfter(mod, revision) : null;
                                startModuleQuiz(mod, nextLesson, examPassed || examCleared ? { practiceOnly: true } : {});
                              }}
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{
                                  fontSize: 18,
                                  color: examPassed || examCleared
                                    ? activeSubjectMeta.color
                                    : examLocked
                                      ? '#94a3b8'
                                      : '#64748b',
                                }}
                              >
                                {examPassed ? 'verified' : examCleared ? 'check_circle' : examLocked ? 'lock' : 'assignment'}
                              </span>
                              <span>
                                {examLocked
                                  ? 'Locked Module Exam'
                                  : examPassed
                                    ? 'Module Exam (passed)'
                                    : examCleared
                                      ? 'Module Exam'
                                      : 'Module Exam'}
                              </span>
                            </button>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })
            )}
            {activeSubject === ER_SUBJECT && extendedResponses.length > 0 ? (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                <p className="sba-course-mod-title" style={{ fontSize: 12, fontWeight: 800, color: '#64748b', margin: '0 0 8px', letterSpacing: '0.03em' }}>
                  EXTENDED RESPONSE
                </p>
                {extendedResponses.map((item, idx) => {
                  const locked = !examsOpen && isErItemLocked(
                    extendedResponses,
                    idx,
                    passedExtendedResponses,
                    clearedExtendedResponses,
                    rlaErUnlocked
                  );
                  const passed = Boolean(passedExtendedResponses[item.id]);
                  const cleared = Boolean(clearedExtendedResponses[item.id]);
                  const active = selectedEr?.id === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`sba-course-lecture${active ? ' is-active' : ''}${locked ? ' is-locked' : ''}${passed || cleared ? ' is-done' : ''}`}
                      style={active ? { borderColor: `${activeSubjectMeta.color}55`, background: `${activeSubjectMeta.color}12` } : undefined}
                      disabled={locked}
                      onClick={() => {
                        if (locked) return;
                        openExtendedResponse(item, {
                          practiceOnly: passed || cleared,
                        });
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: 18,
                          color: passed || cleared
                            ? activeSubjectMeta.color
                            : locked
                              ? '#94a3b8'
                              : activeSubjectMeta.color,
                        }}
                      >
                        {passed ? 'verified' : cleared ? 'check_circle' : locked ? 'lock' : 'edit_note'}
                      </span>
                      <span>
                        {locked
                          ? rlaErUnlocked
                            ? `Locked: ${item.title}`
                            : `After all lectures: ${item.title}`
                          : passed
                            ? `${item.title} (passed)`
                            : item.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
            {activeSubject === IMR_SUBJECT && imageMapReadings.length > 0 ? (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                <p className="sba-course-mod-title" style={{ fontSize: 12, fontWeight: 800, color: '#64748b', margin: '0 0 8px', letterSpacing: '0.03em' }}>
                  IMAGES AND MAP READINGS
                </p>
                {imageMapReadings.map((item, idx) => {
                  const locked = !examsOpen && isImrItemLocked(
                    imageMapReadings,
                    idx,
                    passedImageMapReadings,
                    clearedImageMapReadings,
                    ssImrUnlocked
                  );
                  const passed = Boolean(passedImageMapReadings[item.id]);
                  const cleared = Boolean(clearedImageMapReadings[item.id]);
                  const active = selectedImr?.id === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`sba-course-lecture${active ? ' is-active' : ''}${locked ? ' is-locked' : ''}${passed || cleared ? ' is-done' : ''}`}
                      style={active ? { borderColor: `${activeSubjectMeta.color}55`, background: `${activeSubjectMeta.color}12` } : undefined}
                      disabled={locked}
                      onClick={() => {
                        if (locked) return;
                        openImageMapReading(item, {
                          practiceOnly: passed || cleared,
                        });
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: 18,
                          color: passed || cleared
                            ? activeSubjectMeta.color
                            : locked
                              ? '#94a3b8'
                              : activeSubjectMeta.color,
                        }}
                      >
                        {passed ? 'verified' : cleared ? 'check_circle' : locked ? 'lock' : 'map'}
                      </span>
                      <span>
                        {locked
                          ? ssImrUnlocked
                            ? `Locked: ${item.title}`
                            : `After all lectures: ${item.title}`
                          : passed
                            ? `${item.title} (passed)`
                            : item.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </aside>

        <div className="sba-course-body">
        {selectedImr ? (
          <div style={s.contentWrap}>
            <div style={s.breadcrumb}>
              <span style={{ ...s.breadcrumbSubject, color: activeSubjectMeta.color }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>public</span>
                Social Studies
              </span>
              <span className="material-symbols-outlined" style={s.breadcrumbArrow}>chevron_right</span>
              <span style={s.breadcrumbDay}>{selectedImr.title}</span>
            </div>
            <ImageMapReadingViewer
              key={selectedImr.id}
              item={selectedImr}
              subjectColor={activeSubjectMeta.color}
              passed={Boolean(passedImageMapReadings[selectedImr.id])}
              cleared={Boolean(clearedImageMapReadings[selectedImr.id])}
              practiceOnly={Boolean(passedImageMapReadings[selectedImr.id] || clearedImageMapReadings[selectedImr.id])}
              onStartQuiz={() =>
                startImageMapQuiz(selectedImr, {
                  practiceOnly: Boolean(
                    passedImageMapReadings[selectedImr.id] || clearedImageMapReadings[selectedImr.id]
                  ),
                })
              }
            />
          </div>
        ) : selectedEr ? (
          <div style={s.contentWrap}>
            <div style={s.breadcrumb}>
              <span style={{ ...s.breadcrumbSubject, color: activeSubjectMeta.color }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>menu_book</span>
                Language Arts
              </span>
              <span className="material-symbols-outlined" style={s.breadcrumbArrow}>chevron_right</span>
              <span style={s.breadcrumbDay}>{selectedEr.title}</span>
            </div>
            <ExtendedResponseWriter
              key={selectedEr.id}
              item={selectedEr}
              subjectColor={activeSubjectMeta.color}
              attemptsUsed={erAttemptsUsed}
              result={erResult}
              practiceOnly={erPracticeOnly}
              submitting={erSubmitting}
              onSubmit={submitExtendedResponse}
              onRetry={() => setErResult(null)}
              onContinueAnyway={continueAfterErFail}
              onCloseResults={closeErResults}
            />
          </div>
        ) : selectedDay ? (
          <div style={s.contentWrap}>
            {/* Breadcrumb */}
            {(() => {
              const subject = SUBJECTS.find((sub) => sub.id === selectedDay.module.subject);
              const isAiPowered = !!selectedDay.day.topic && !hasAuthoredLecture(selectedDay.day);
              const authoredBlocks = hasAuthoredLecture(selectedDay.day)
                ? selectedDay.day.contentBlocks
                : null;
              return (
                <>
                  <div style={s.breadcrumb}>
                    <span style={{ ...s.breadcrumbSubject, color: subject?.color || SBA.navy }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{subject?.icon || 'school'}</span>
                      {subject?.label}
                    </span>
                    <span className="material-symbols-outlined" style={s.breadcrumbArrow}>chevron_right</span>
                    <span style={s.breadcrumbModule}>{selectedDay.module.title}</span>
                    <span className="material-symbols-outlined" style={s.breadcrumbArrow}>chevron_right</span>
                    <span style={s.breadcrumbDay}>{selectedDay.day.title}</span>
                  </div>

                  {/* Header */}
                  <div style={s.lessonHeader}>
                    <div style={s.lessonPillRow}>
                      <div style={{ ...s.subjectPill, background: (subject?.color || SBA.navy) + '18', color: subject?.color || SBA.navy }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                          {isRevisionLecture ? 'replay' : 'menu_book'}
                        </span>
                        {isRevisionLecture ? 'Revision Lecture' : 'Lecture'}
                      </div>
                      {isRevisionLecture && (
                        <div style={{ ...s.aiPill, color: '#b45309', borderColor: '#f59e0b55', background: '#fffbeb' }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>auto_stories</span>
                          Revises {priorLecturesForRevision.length} prior lecture{priorLecturesForRevision.length === 1 ? '' : 's'}
                        </div>
                      )}
                      {isAiPowered && <div style={s.aiPill}><span className="material-symbols-outlined" style={{ fontSize: 15 }}>auto_awesome</span>AI-Powered</div>}
                      {isAiPowered && (
                        <button onClick={handleRegenerate} style={s.regenBtn} disabled={aiLoading || aiStreaming}>
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>refresh</span>
                          {isRevisionLecture ? 'New revision' : 'New lesson'}
                        </button>
                      )}
                      {!aiLoading && !aiError && aiContent ? (
                        <LectureTtsControls
                          text={aiContent}
                          resetKey={`${selectedDay.module.id}-${selectedDay.day.id}-${regenerateKey}`}
                          enabled={!aiStreaming}
                          accentColor={subject?.color || SBA.navy}
                        />
                      ) : null}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <h1 style={{ ...s.lessonTitle, margin: 0 }}>{selectedDay.day.title}</h1>
                      <HeadingSpeakButton text={selectedDay.day.title} />
                    </div>
                    <p style={s.lessonSubtitle}>
                      {isRevisionLecture
                        ? `${selectedDay.module.title} · Module revision of all previous lectures`
                        : selectedDay.module.title}
                    </p>
                  </div>

                  <div style={s.contentDivider} />

                  {/* Instructor worked-example images */}
                  {(() => {
                    const solvedImages = normalizeSolvedImages(selectedDay.day);
                    if (!solvedImages.length) return null;
                    return (
                      <div style={s.solvedImageSection}>
                        <div style={s.solvedImageHeader}>
                          <span className="material-symbols-outlined" style={{ fontSize: 20, color: subject?.color || SBA.navy }}>
                            image
                          </span>
                          <h3 style={{ ...s.solvedImageTitle, flex: 1 }}>
                            Worked Example{solvedImages.length > 1 ? 's' : ''} (from your instructor)
                          </h3>
                          <HeadingSpeakButton
                            text={`Worked examples. ${solvedImages.map((img) => img.description).filter(Boolean).join(' ')}`}
                          />
                        </div>
                        <LectureImageRow
                          images={solvedImages.map((img) => ({
                            ...img,
                            imageUrl: img.url,
                            alt: img.description || '',
                          }))}
                        />
                      </div>
                    );
                  })()}

                  {/* AI Loading */}
                  {aiLoading && (
                    <div style={s.aiLoadingBox}>
                      <div style={s.aiLoadingIconWrap}>
                        <span className="material-symbols-outlined" style={{ fontSize: 32, color: subject?.color || SBA.navy }}>auto_awesome</span>
                      </div>
                      <p style={s.aiLoadingTitle}>
                        {isRevisionLecture
                          ? 'Your teacher is preparing a module revision…'
                          : 'Your teacher is preparing today\u2019s lesson…'}
                      </p>
                      <p style={s.aiLoadingSub}>
                        {isRevisionLecture
                          ? (
                            <>
                              Revising all {priorLecturesForRevision.length} earlier lecture
                              {priorLecturesForRevision.length === 1 ? '' : 's'} in{' '}
                              <em>&quot;{selectedDay.module.title}&quot;</em>
                            </>
                          )
                          : (
                            <>
                              AI is teaching <em>&quot;{selectedDay.day.topic}&quot;</em> — saved for you after the first open
                            </>
                          )}
                      </p>
                      <div style={s.aiLoadingBar}><div style={{ ...s.aiLoadingFill, background: subject?.color || SBA.navy }} /></div>
                    </div>
                  )}

                  {/* AI Error */}
                  {!aiLoading && aiError && (
                    <div style={s.aiErrorBox}>
                      <span className="material-symbols-outlined" style={{ fontSize: 24, color: '#ef4444' }}>error</span>
                      <div>
                        <p style={s.aiErrorTitle}>Could not generate content</p>
                        <p style={s.aiErrorMsg}>{aiError}</p>
                      </div>
                      <button onClick={handleRegenerate} style={s.retryBtn}>
                        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>refresh</span>Try Again
                      </button>
                    </div>
                  )}

                  {/* Lecture text */}
                  {!aiLoading && !aiError && aiContent !== null && (
                    <div style={s.lectureBody}>
                      {aiContent
                        ? <>
                            <LectureRenderer
                              content={aiContent}
                              blocks={authoredBlocks}
                              color={subject?.color || SBA.navy}
                              isStreaming={aiStreaming}
                            />
                          </>
                        : <p style={s.noContent}>No content for this lecture yet.</p>}
                    </div>
                  )}

                  {/* Wikipedia visual reference */}
                  {!aiLoading && aiContent && selectedDay.day.topic && !hasAuthoredLecture(selectedDay.day) && (
                    <WikiImages
                      topic={selectedDay.day.topic}
                      lectureTitle={selectedDay.day.title}
                    />
                  )}

                  {/* ── YouTube Videos ── */}
                  {!aiLoading && (
                    <YouTubeSection videos={normalizeYoutubeVideos(selectedDay.day.youtubeVideos)} />
                  )}

                  {/* ── Mark Complete ── */}
                  {!aiLoading && (
                    <div style={s.completionBar}>
                      {isRevisionLecture ? (
                        isCurrentLessonDone ? (
                          <div style={s.doneBadge}>
                            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>check_circle</span>
                            Revision complete — start the Module Exam when you feel ready
                          </div>
                        ) : (
                          <button
                            onClick={markComplete}
                            disabled={marking || aiLoading}
                            style={{ ...s.markBtn, background: subject?.color || SBA.navy, opacity: marking ? 0.6 : 1 }}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                              {marking ? 'hourglass_empty' : 'check_circle'}
                            </span>
                            {marking ? 'Saving…' : 'Mark as Complete'}
                          </button>
                        )
                      ) : isCurrentLessonDone && isLectureQuizPassed(selectedDay.day.id) ? (
                        <div style={{ display: 'grid', gap: 10, width: '100%' }}>
                          <div style={s.doneBadge}>
                            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>check_circle</span>
                            {passedLectureQuizzes[selectedDay.day.id]
                              ? 'Lecture + quiz completed'
                              : 'Lecture completed — you can continue after two quiz tries'}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              startLectureQuiz({
                                module: selectedDay.module,
                                day: selectedDay.day,
                                nextLesson: nextLessonAfter(selectedDay.module, selectedDay.day),
                                practiceOnly: true,
                              });
                            }}
                            style={{ ...s.markBtn, background: 'transparent', color: subject?.color || SBA.navy, border: `1.5px solid ${subject?.color || SBA.navy}` }}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>replay</span>
                            Retake saved lecture quiz
                          </button>
                        </div>
                      ) : isCurrentLessonDone ? (
                        <button
                          type="button"
                          onClick={() => {
                            startLectureQuiz({
                              module: selectedDay.module,
                              day: selectedDay.day,
                              nextLesson: nextLessonAfter(selectedDay.module, selectedDay.day),
                            });
                          }}
                          style={{ ...s.markBtn, background: subject?.color || SBA.navy }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>quiz</span>
                          Take lecture quiz ({Math.min(LECTURE_QUIZ_DRAW, toAuthoredQuizQuestions(selectedDay.day.quizQuestions).length) || LECTURE_QUIZ_DRAW} questions)
                        </button>
                      ) : (
                        <button
                          onClick={markComplete}
                          disabled={marking || aiLoading}
                          style={{ ...s.markBtn, background: subject?.color || SBA.navy, opacity: marking ? 0.6 : 1 }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                            {marking ? 'hourglass_empty' : 'check_circle'}
                          </span>
                          {marking ? 'Saving…' : 'Mark as Complete'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Module exam — unlocks after revision is marked complete */}
                  {!aiLoading &&
                    isModuleExamUnlocked(selectedDay.module) &&
                    !isModuleQuizPassed(selectedDay.module.id) &&
                    !clearedModuleQuizzes[selectedDay.module.id] && (
                      <div style={{ marginBottom: 24 }}>
                        <button
                          type="button"
                          onClick={() => {
                            const revision =
                              (daysByModule[selectedDay.module.id] || []).find((d) => d.isRevisionLecture)
                              || selectedDay.day;
                            startModuleQuiz(
                              selectedDay.module,
                              nextLessonAfter(selectedDay.module, revision)
                            );
                          }}
                          style={{
                            ...s.markBtn,
                            width: '100%',
                            justifyContent: 'center',
                            background: subject?.color || SBA.navy,
                          }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>assignment</span>
                          Take Module Exam
                        </button>
                      </div>
                    )}

                  {passedModuleQuizzes[selectedDay?.module?.id] && (
                    <div style={{ marginBottom: 24, display: 'grid', gap: 10 }}>
                      <div style={s.doneBadge}>
                        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>emoji_events</span>
                        Module Exam passed
                      </div>
                      <button
                        type="button"
                        onClick={() => startModuleQuiz(selectedDay.module, null, { practiceOnly: true })}
                        style={{
                          ...s.markBtn,
                          width: '100%',
                          justifyContent: 'center',
                          background: 'transparent',
                          color: subject?.color || SBA.navy,
                          border: `1.5px solid ${subject?.color || SBA.navy}`,
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>replay</span>
                        Retake saved Module Exam
                      </button>
                    </div>
                  )}
                  {!passedModuleQuizzes[selectedDay?.module?.id] &&
                    clearedModuleQuizzes[selectedDay?.module?.id] && (
                    <div style={{ marginBottom: 24, display: 'grid', gap: 10 }}>
                      <div style={s.doneBadge}>
                        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>check_circle</span>
                        Module Exam not passed — you can retry or continue to the next module
                      </div>
                      <button
                        type="button"
                        onClick={() => startModuleQuiz(selectedDay.module, null, { practiceOnly: true })}
                        style={{
                          ...s.markBtn,
                          width: '100%',
                          justifyContent: 'center',
                          background: 'transparent',
                          color: subject?.color || SBA.navy,
                          border: `1.5px solid ${subject?.color || SBA.navy}`,
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>replay</span>
                        Retry Module Exam
                      </button>
                    </div>
                  )}

                  {/* ── AI Chat ── */}
                  {!aiLoading && aiContent && (
                    <ChatBox
                      messages={chatMessages}
                      streamingContent={chatStreamContent}
                      streaming={chatStreaming}
                      input={chatInput}
                      setInput={setChatInput}
                      loading={chatLoading}
                      onSend={sendChatMessage}
                      subject={selectedDay.module.subject}
                    />
                  )}

                  {/* ── Prev / Next nav ── */}
                  {!aiLoading && (
                    <div style={s.navRow}>
                      {(() => {
                        const onPrev = getAdjacentLesson('prev');
                        const onNext = getAdjacentLesson('next');
                        return (
                          <>
                            {onPrev
                              ? <button onClick={() => selectLesson(onPrev.module, onPrev.day, false)} style={s.navBtn(false)}>
                                  <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_back</span>
                                  <div><p style={s.navBtnLabel}>Previous</p><p style={s.navBtnTitle}>{onPrev.day.title}</p></div>
                                </button>
                              : <div />}
                            {onNext
                              ? <button onClick={() => handleNextLectureClick(onNext)} style={s.navBtn(true)}>
                                  <div style={{ textAlign: 'right' }}><p style={s.navBtnLabel}>Next Lecture</p><p style={s.navBtnTitle}>{onNext.day.title}</p></div>
                                  <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_forward</span>
                                </button>
                              : isCurrentLessonDone
                                ? (
                                    <div style={s.courseCompleteTip}>
                                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>emoji_events</span>
                                      You&apos;ve reached the end!
                                    </div>
                                  )
                                : (
                                  <div style={s.nextLockTip}>
                                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>lock</span>
                                    Complete this lecture to unlock the next one
                                  </div>
                                )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ) : (
          <WelcomeScreen
            modules={modules}
            daysByModule={daysByModule}
            completedLessons={completedLessons}
            onSelectSubject={selectSubject}
          />
        )}
        </div>
      </div>

      {/* ── Quiz Modal ── */}
      {quizState && quizKind === 'module' ? (
        <ModuleExamPlayer
          state={quizState}
          questions={quizData}
          answers={quizAnswers}
          onAnswer={handleQuizAnswer}
          onSubmit={submitQuiz}
          onProceed={proceedAfterQuizPass}
          onRetry={retryQuiz}
          onContinueAnyway={continueAfterQuizFail}
          onClose={closeQuizWithoutPass}
          canSkip={!quizPracticeOnly && quizAttemptsUsed >= MAX_QUIZ_TRIES_BEFORE_SKIP}
          canRetry
          practiceOnly={quizPracticeOnly}
          color={(SUBJECTS.find((item) => item.id === (quizContext?.module?.subject || selectedDay?.module?.subject)) || {}).color || '#6332ed'}
          title="Module Exam"
          subtitle={`${quizContext?.title || 'Module'} — ${quizContext?.questionCount || MODULE_EXAM_DRAW} questions this try.`}
          passMark={quizPassMark(quizData, quizKind)}
          proceedLabel={pendingNextLesson ? 'Continue to Next Module' : 'Done'}
          continueLabel={pendingNextLesson ? 'Move to next module' : 'Continue Anyway'}
        />
      ) : null}
      {quizState && quizKind !== 'module' && (
        <QuizModal
          state={quizState}
          questions={quizData}
          answers={quizAnswers}
          onAnswer={handleQuizAnswer}
          onSubmit={submitQuiz}
          onProceed={proceedAfterQuizPass}
          onRetry={retryQuiz}
          onContinueAnyway={continueAfterQuizFail}
          onClose={closeQuizWithoutPass}
          canSkip={!quizPracticeOnly && quizAttemptsUsed >= MAX_QUIZ_TRIES_BEFORE_SKIP}
          canRetry={
            quizPracticeOnly
            || quizKind === 'module'
            || quizKind === 'image-map'
            || quizAttemptsUsed < MAX_QUIZ_TRIES_BEFORE_SKIP
          }
          practiceOnly={quizPracticeOnly}
          subject={quizContext?.module?.subject || selectedDay?.module?.subject}
          title={
            quizKind === 'module'
              ? 'Module Exam'
              : quizKind === 'image-map'
                ? 'Image & map MCQs'
                : 'Lecture Quiz'
          }
          subtitle={
            quizKind === 'module'
              ? `${quizContext?.title || 'Module'} — ${quizContext?.questionCount || MODULE_EXAM_DRAW} questions this try, equally from each lecture quiz (plus extras). Shuffled. Retry draws a new set. No AI.`
              : quizKind === 'image-map'
                ? `${quizContext?.title || 'Visual image'} — questions from this map or image. No AI. Pass to continue.`
                : `${quizContext?.title || selectedDay?.day?.title || 'Lecture'} — ${quizContext?.questionCount || LECTURE_QUIZ_DRAW} questions this try${
                    quizContext?.poolCount > LECTURE_QUIZ_DRAW ? ` (from a pool of ${quizContext.poolCount})` : ''
                  }. Retry draws a new shuffled set. Pass to move on.`
          }
          quizKind={quizKind}
          isRevision={false}
          passMark={quizPassMark(quizData, quizKind)}
          proceedLabel={
            quizKind === 'module'
              ? pendingNextLesson
                ? 'Continue to Next Module'
                : 'Done'
              : quizKind === 'image-map'
                ? 'Continue'
                : pendingNextLesson
                  ? 'Continue to Next Lecture'
                  : 'Done'
          }
          continueLabel={
            quizKind === 'module'
              ? pendingNextLesson
                ? 'Move to next module'
                : 'Continue Anyway'
              : quizKind === 'image-map'
                ? 'Continue Anyway'
                : pendingNextLesson
                  ? 'Go to the next lecture'
                  : 'Continue Anyway'
          }
        />
      )}
      {erPrompt ? (
        <div style={qm.overlay} role="dialog" aria-labelledby="er-prompt-title">
          <div style={{ ...qm.modal, maxWidth: 440, padding: 0 }}>
            <div style={{ ...qm.header, borderTop: `3px solid ${SBA.navy}` }}>
              <div style={qm.headerLeft}>
                <span className="material-symbols-outlined" style={{ fontSize: 24, color: SBA.navy }}>edit_note</span>
                <div>
                  <h2 id="er-prompt-title" style={qm.headerTitle}>Language Arts modules complete</h2>
                  <p style={qm.headerSub}>{erPrompt.item?.title || 'Extended Response'}</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '20px 24px 8px', fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
              Would you like to practice the Extended Response now, or wait until you feel ready?
              You can start it later from the Language Arts list.
            </div>
            <div style={{ ...qm.footerCol, padding: 20 }}>
              <button
                type="button"
                onClick={() => {
                  const prompt = erPrompt;
                  setErPrompt(null);
                  openExtendedResponse(prompt.item);
                }}
                style={{ ...qm.proceedBtn, background: SBA.navy }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 17 }}>edit_note</span>
                Practice Extended Response now
              </button>
              <button
                type="button"
                onClick={() => setErPrompt(null)}
                style={{ ...qm.retryBtnOutline, borderColor: SBA.navy, color: SBA.navy }}
              >
                Not now — I&apos;ll take it later
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {imrPrompt ? (
        <div style={qm.overlay} role="dialog" aria-labelledby="imr-prompt-title">
          <div style={{ ...qm.modal, maxWidth: 440, padding: 0 }}>
            <div style={{ ...qm.header, borderTop: `3px solid ${SBA.navy}` }}>
              <div style={qm.headerLeft}>
                <span className="material-symbols-outlined" style={{ fontSize: 24, color: SBA.navy }}>map</span>
                <div>
                  <h2 id="imr-prompt-title" style={qm.headerTitle}>Social Studies modules complete</h2>
                  <p style={qm.headerSub}>{imrPrompt.item?.title || 'Images and map Readings'}</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '20px 24px 8px', fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
              Would you like to practice Images and map Readings now, or wait until you feel ready?
              You can start it later from the Social Studies list.
            </div>
            <div style={{ ...qm.footerCol, padding: 20 }}>
              <button
                type="button"
                onClick={() => {
                  const prompt = imrPrompt;
                  setImrPrompt(null);
                  openImageMapReading(prompt.item);
                }}
                style={{ ...qm.proceedBtn, background: SBA.navy }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 17 }}>map</span>
                Practice map reading now
              </button>
              <button
                type="button"
                onClick={() => setImrPrompt(null)}
                style={{ ...qm.retryBtnOutline, borderColor: SBA.navy, color: SBA.navy }}
              >
                Not now — I&apos;ll take it later
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {examPrompt ? (
        <div style={qm.overlay} role="dialog" aria-labelledby="exam-prompt-title">
          <div style={{ ...qm.modal, maxWidth: 440, padding: 0 }}>
            <div style={{ ...qm.header, borderTop: `3px solid ${SBA.navy}` }}>
              <div style={qm.headerLeft}>
                <span className="material-symbols-outlined" style={{ fontSize: 24, color: SBA.navy }}>assignment</span>
                <div>
                  <h2 id="exam-prompt-title" style={qm.headerTitle}>Revision complete</h2>
                  <p style={qm.headerSub}>{examPrompt.moduleTitle || 'This module'}</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '20px 24px 8px', fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
              Would you like to take the Module Exam now, or wait until you feel ready?
              You can start it later from the module list.
            </div>
            <div style={{ ...qm.footerCol, padding: 20 }}>
              <button
                type="button"
                onClick={() => {
                  const prompt = examPrompt;
                  setExamPrompt(null);
                  startModuleQuiz(prompt.module, prompt.nextLesson);
                }}
                style={{ ...qm.proceedBtn, background: SBA.navy }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 17 }}>quiz</span>
                Take Module Exam now
              </button>
              <button
                type="button"
                onClick={() => setExamPrompt(null)}
                style={{ ...qm.retryBtnOutline, borderColor: SBA.navy, color: SBA.navy }}
              >
                Not now — I&apos;ll take it later
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showFocusPopup ? <StudyFocusPopup onDismiss={dismissFocusPopup} /> : null}
    </AppShell>
  );
}

/* ══════════════════════════════════════════
   KaTeX Math Helpers
══════════════════════════════════════════ */
function SafeInlineMath({ math }) {
  try {
    return <InlineMath math={math} />;
  } catch {
    return <code style={{ fontFamily: 'monospace', background: '#f6f3f5', padding: '1px 5px', borderRadius: 4, fontSize: 13 }}>{math}</code>;
  }
}

function SafeBlockMath({ math }) {
  try {
    return <BlockMath math={math} />;
  } catch {
    return <pre style={{ fontFamily: 'monospace', background: '#f6f3f5', padding: 12, borderRadius: 8, overflowX: 'auto', fontSize: 13 }}>{math}</pre>;
  }
}

/** Render inline text with $math$, **bold**, and *emphasis* (shown as bold). */
function RichInlineText({ text }) {
  if (!text) return null;

  // Protect math first so * inside formulas is not treated as markdown
  const mathParts = String(text).split(/(\$\$[\s\S]*?\$\$|\$[^$\n]+\$)/g);

  return (
    <>
      {mathParts.map((part, i) => {
        if (!part) return null;
        if (part.startsWith('$$') && part.endsWith('$$') && part.length > 4) {
          return (
            <span key={i} style={{ display: 'block', margin: '6px 0' }}>
              <SafeBlockMath math={part.slice(2, -2).trim()} />
            </span>
          );
        }
        if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
          return <SafeInlineMath key={i} math={part.slice(1, -1).trim()} />;
        }
        return <RichMarkdownChunk key={i} text={part} />;
      })}
    </>
  );
}

function RichMarkdownChunk({ text }) {
  // **bold** then *bold/emphasis* (single asterisks used by the model for emphasis)
  const parts = String(text).split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null;
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
          return (
            <strong key={i} style={{ fontWeight: 800, color: 'inherit' }}>
              {part.slice(2, -2)}
            </strong>
          );
        }
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
          return (
            <strong key={i} style={{ fontWeight: 800, color: 'inherit' }}>
              {part.slice(1, -1)}
            </strong>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

/** @deprecated alias — kept for clarity in older call sites */
function InlineMathText({ text }) {
  return <RichInlineText text={text} />;
}

function latexTableRows(body) {
  const cleaned = String(body || '')
    .replace(/\\hline/g, '')
    .replace(/\\cline\{[^}]*\}/g, '')
    .trim();
  return cleaned
    .split(/\\\\/)
    .map((row) => row.trim())
    .filter(Boolean)
    .map((row) => row.split('&').map((cell) => cell.trim()));
}

function isPipeRow(line) {
  const trimmed = String(line || '').trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 2;
}

function isPipeSeparator(line) {
  return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(String(line || '').trim());
}

function pipeCells(line) {
  return String(line || '').trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

function segmentLectureSource(content) {
  const text = String(content || '');
  const pieces = [];
  const latexRe = /\\begin\{(tabular|array)\*?\}(?:\[[^\]]*\])?(?:\{[^}]*\})?([\s\S]*?)\\end\{\1\*?\}/g;
  let last = 0;
  let match = latexRe.exec(text);
  while (match) {
    if (match.index > last) pieces.push({ type: 'text', content: text.slice(last, match.index) });
    const rows = latexTableRows(match[2]);
    if (rows.length) pieces.push({ type: 'table', rows });
    last = match.index + match[0].length;
    match = latexRe.exec(text);
  }
  if (last < text.length) pieces.push({ type: 'text', content: text.slice(last) });

  const out = [];
  pieces.forEach((piece) => {
    if (piece.type === 'table') {
      out.push(piece);
      return;
    }
    const lines = piece.content.split('\n');
    let buffer = [];
    let table = [];
    const flushText = () => {
      if (!buffer.length) return;
      out.push({ type: 'text', content: buffer.join('\n') });
      buffer = [];
    };
    const flushTable = () => {
      if (!table.length) return;
      out.push({ type: 'table', rows: table });
      table = [];
    };
    lines.forEach((line) => {
      if (isPipeSeparator(line)) return;
      if (isPipeRow(line)) {
        flushText();
        table.push(pipeCells(line));
        return;
      }
      flushTable();
      buffer.push(line);
    });
    flushTable();
    flushText();
  });
  return out;
}

/* ──────────────────────────────────────────
   Content parser: splits raw text into typed
   segments (header, display-math, step, paragraph)
────────────────────────────────────────── */
function parseContent(content) {
  const segments = [];
  segmentLectureSource(content).forEach((piece) => {
    if (piece.type === 'table') {
      segments.push(piece);
      return;
    }
    const parts = String(piece.content || '').split(/(\$\$[\s\S]*?\$\$)/g);
    parts.forEach((part) => {
      if (part.startsWith('$$') && part.endsWith('$$')) {
        const math = part.slice(2, -2).trim();
        if (math) segments.push({ type: 'display-math', content: math });
        return;
      }
      const lines = part.split('\n');
      lines.forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed) {
          segments.push({ type: 'spacer' });
          return;
        }
        if (trimmed.startsWith('[[') && trimmed.endsWith(']]')) {
          const inner = trimmed.slice(2, -2).trim();
          const imageMatch = inner.match(
            /^IMAGE\s+url="([^"]*)"(?:\s+alt="([^"]*)")?(?:\s+description="([^"]*)")?\s*$/i
          );
          if (imageMatch) {
            segments.push({
              type: 'image',
              url: imageMatch[1],
              alt: imageMatch[2] || '',
              description: imageMatch[3] || '',
            });
            return;
          }
          segments.push({ type: 'header', content: inner });
          return;
        }
        const stepMatch = trimmed.match(/^(Step\s+\d+[:.]?)\s*(.*)/is);
        if (stepMatch) {
          segments.push({ type: 'step', label: stepMatch[1], content: stepMatch[2] });
          return;
        }
        const numMatch = trimmed.match(/^(\d+\.)\s+(.*)/);
        if (numMatch) {
          segments.push({ type: 'numbered', label: numMatch[1], content: numMatch[2] });
          return;
        }
        const bulletMatch = trimmed.match(/^[-•]\s+(.*)/);
        if (bulletMatch) {
          segments.push({ type: 'bullet', content: bulletMatch[1] });
          return;
        }
        segments.push({ type: 'paragraph', content: trimmed });
      });
    });
  });

  while (segments.length && segments[0].type === 'spacer') segments.shift();
  while (segments.length && segments[segments.length - 1].type === 'spacer') segments.pop();

  return segments;
}

/* ──────────────────────────────────────────
   Lecture Renderer
────────────────────────────────────────── */
const LECTURE_IMAGE_PAGE = 3;

function photoDescription(img) {
  return String(img?.description || img?.alt || '').trim();
}

function HeadingSpeakButton({ text }) {
  const { supported, speakingId, speak } = useChatMessageTts();
  const spoken = lectureContentToSpeechText(String(text || ''));
  if (!supported || !spoken) return null;
  const active = speakingId === 'heading';
  return (
    <button
      type="button"
      onClick={() => speak('heading', spoken)}
      style={lr.headingSpeak(active)}
      title={active ? 'Stop reading this heading' : 'Listen to this heading'}
      aria-label={active ? 'Stop reading this heading' : 'Listen to this heading'}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
        {active ? 'stop_circle' : 'volume_up'}
      </span>
    </button>
  );
}

function HeadingWithTts({ color, text, children }) {
  return (
    <h3 style={{ ...lr.sectionHeader(color), display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <span>{children}</span>
      <HeadingSpeakButton text={text} />
    </h3>
  );
}

function LecturePhotoWindow({ images, startIndex = 0, onClose }) {
  const pics = (images || []).filter((img) => img?.imageUrl || img?.url);
  const [index, setIndex] = useState(Math.min(startIndex, Math.max(pics.length - 1, 0)));
  useEffect(() => {
    setIndex(Math.min(startIndex, Math.max(pics.length - 1, 0)));
  }, [startIndex, pics.length]);
  useEffect(() => {
    function onKey(event) {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') setIndex((value) => Math.min(pics.length - 1, value + 1));
      if (event.key === 'ArrowLeft') setIndex((value) => Math.max(0, value - 1));
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pics.length]);
  const img = pics[index];
  if (!img) return null;
  const description = photoDescription(img);
  return createPortal(
    <div style={photo.overlay} onClick={onClose} role="presentation">
      <div style={photo.panel} onClick={(event) => event.stopPropagation()} role="dialog" aria-label="Lecture photo gallery">
        <div style={photo.stage}>
          <img src={img.imageUrl || img.url} alt={description} style={photo.image} />
          {pics.length > 1 ? (
            <>
              <button type="button" style={{ ...photo.arrow, left: 10 }} onClick={() => setIndex((value) => Math.max(0, value - 1))} disabled={index === 0} aria-label="Previous photo">
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <button type="button" style={{ ...photo.arrow, right: 10 }} onClick={() => setIndex((value) => Math.min(pics.length - 1, value + 1))} disabled={index === pics.length - 1} aria-label="Next photo">
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </>
          ) : null}
        </div>
        <p style={photo.caption}>{description || 'No description was added for this photo.'}</p>
        {pics.length > 1 ? (
          <div style={photo.thumbs} aria-label="Photo gallery">
            {pics.map((item, itemIndex) => (
              <button
                key={item.id || itemIndex}
                type="button"
                onClick={() => setIndex(itemIndex)}
                style={{
                  ...photo.thumbBtn,
                  borderColor: itemIndex === index ? '#1b365d' : '#e2e8f0',
                }}
                aria-label={`Show photo ${itemIndex + 1}`}
              >
                <img src={item.imageUrl || item.url} alt="" style={photo.thumb} />
              </button>
            ))}
          </div>
        ) : null}
        <div style={photo.actions}>
          <span style={photo.count}>{index + 1} of {pics.length}</span>
          <button type="button" style={photo.close} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const photo = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 4500,
    background: 'rgba(15, 23, 42, 0.72)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  panel: {
    width: 'min(980px, 100%)',
    maxHeight: '92vh',
    overflow: 'auto',
    background: '#fff',
    borderRadius: 18,
    padding: 18,
    boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
  },
  stage: { position: 'relative' },
  image: {
    width: '100%',
    maxHeight: '58vh',
    objectFit: 'contain',
    borderRadius: 12,
    background: '#f8fafc',
    display: 'block',
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 40,
    height: 40,
    borderRadius: 999,
    border: 'none',
    background: 'rgba(255,255,255,0.94)',
    color: '#1b365d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
  },
  caption: {
    margin: '14px 0 0',
    fontSize: 15,
    lineHeight: 1.6,
    color: '#1b1b1d',
    fontWeight: 600,
  },
  thumbs: { display: 'flex', gap: 8, overflowX: 'auto', marginTop: 14, paddingBottom: 4 },
  thumbBtn: {
    flex: '0 0 auto',
    width: 84,
    height: 64,
    padding: 0,
    borderRadius: 8,
    border: '2px solid #e2e8f0',
    background: '#f8fafc',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  thumb: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  actions: { display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginTop: 16, flexWrap: 'wrap' },
  count: { color: '#64748b', fontSize: 13, fontWeight: 700 },
  close: {
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#334155',
    borderRadius: 10,
    padding: '10px 14px',
    fontWeight: 700,
    cursor: 'pointer',
  },
};

function LectureImageRow({ images }) {
  const pics = (images || []).filter((img) => img?.imageUrl || img?.url);
  const [page, setPage] = useState(0);
  const [galleryIndex, setGalleryIndex] = useState(null);
  const [infoIndex, setInfoIndex] = useState(null);
  if (!pics.length) return null;
  const pageCount = Math.max(1, Math.ceil(pics.length / LECTURE_IMAGE_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * LECTURE_IMAGE_PAGE;
  const visible = pics.slice(start, start + LECTURE_IMAGE_PAGE);
  return (
    <div>
      <div style={lr.imageRow}>
        {visible.map((img, localIndex) => {
          const index = start + localIndex;
          const description = photoDescription(img);
          const showTotal = pics.length > LECTURE_IMAGE_PAGE && safePage === 0 && localIndex === visible.length - 1;
          return (
            <figure key={img.id || index} style={lr.imageCell}>
              <div style={{ position: 'relative' }}>
                <button type="button" onClick={() => setGalleryIndex(index)} style={lr.imageOpen} aria-label={`Open photo ${index + 1} in gallery`}>
                  <SafeImage
                    src={img.imageUrl || img.url}
                    alt={description}
                    style={pics.length > 1 ? lr.imageInRow : lr.image}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => setInfoIndex((current) => (current === index ? null : index))}
                  style={lr.imageInfo}
                  title="See what is in this image"
                  aria-label="See what is in this image"
                  aria-expanded={infoIndex === index}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>visibility</span>
                </button>
                {showTotal ? (
                  <span style={lr.imageCount} aria-hidden="true">{pics.length}</span>
                ) : null}
              </div>
              {infoIndex === index ? (
                <figcaption style={lr.imageCap}>{description || 'No description was added for this photo.'}</figcaption>
              ) : null}
            </figure>
          );
        })}
      </div>
      {pageCount > 1 ? (
        <div style={lr.imagePager}>
          <button type="button" onClick={() => setPage((value) => Math.max(0, value - 1))} disabled={safePage === 0} style={{ ...lr.pageBtn, opacity: safePage === 0 ? 0.45 : 1 }}>
            Previous
          </button>
          <span style={lr.pageLabel}>Page {safePage + 1} of {pageCount}</span>
          <button type="button" onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))} disabled={safePage === pageCount - 1} style={{ ...lr.pageBtn, opacity: safePage === pageCount - 1 ? 0.45 : 1 }}>
            Next
          </button>
        </div>
      ) : null}
      {galleryIndex != null ? (
        <LecturePhotoWindow images={pics} startIndex={galleryIndex} onClose={() => setGalleryIndex(null)} />
      ) : null}
    </div>
  );
}

function groupLectureSegments(segments) {
  const out = [];
  (segments || []).forEach((seg) => {
    if (seg.type === 'image') {
      const last = out[out.length - 1];
      if (last?.type === 'image-row') {
        last.images.push(seg);
      } else {
        out.push({ type: 'image-row', images: [seg] });
      }
      return;
    }
    out.push(seg);
  });
  return out;
}

function segmentPlainText(seg) {
  if (!seg) return '';
  if (seg.type === 'header' || seg.type === 'paragraph' || seg.type === 'bullet') return seg.content || '';
  if (seg.type === 'step' || seg.type === 'numbered') return `${seg.label || ''} ${seg.content || ''}`.trim();
  if (seg.type === 'image-row') {
    return (seg.images || []).map((img) => photoDescription(img)).filter(Boolean).join('. ');
  }
  if (seg.type === 'table') {
    return (seg.rows || []).map((row) => (row || []).join(', ')).join('. ');
  }
  return seg.content || '';
}

function headingSectionSpeech(segments, index) {
  const parts = [];
  for (let i = index; i < segments.length; i += 1) {
    if (i !== index && segments[i].type === 'header') break;
    const bit = segmentPlainText(segments[i]);
    if (bit) parts.push(bit);
  }
  return parts.join('\n');
}

function LectureRenderer({ content, blocks, color, isStreaming }) {
  if (Array.isArray(blocks) && blocks.length) {
    const folded = foldLectureBlocks(blocks);
    return (
      <>
        {folded.map((block) => {
          if (block.type === 'image' && block.imageUrl) {
            return <LectureImageRow key={block.id} images={[block]} />;
          }
          if (block.type === 'section') {
            return (
              <div key={block.id}>
                {block.heading ? (
                  <HeadingWithTts color={color} text={`${block.heading}\n${block.text || ''}`}>
                    {block.heading}
                  </HeadingWithTts>
                ) : null}
                <LectureRenderer content={block.text || ''} color={color} />
                <LectureImageRow images={sectionImages(block)} />
              </div>
            );
          }
          return null;
        })}
        {isStreaming && <span className="ged-cursor" style={{ color }} />}
      </>
    );
  }

  const segments = groupLectureSegments(parseContent(content));
  let key = 0;

  const els = segments.map((seg, segIndex) => {
    switch (seg.type) {
      case 'spacer':
        return <div key={key++} style={{ height: 8 }} />;

      case 'header':
        return (
          <HeadingWithTts key={key++} color={color} text={headingSectionSpeech(segments, segIndex)}>
            {seg.content}
          </HeadingWithTts>
        );

      case 'display-math':
        return (
          <div key={key++} style={lr.mathBlock}>
            <SafeBlockMath math={seg.content} />
          </div>
        );

      case 'step':
        return (
          <div key={key++} style={lr.stepRow}>
            <span style={lr.stepBadge(color)}>{seg.label}</span>
            <p style={lr.stepText}>
              <RichInlineText text={seg.content} />
            </p>
          </div>
        );

      case 'numbered':
        return (
          <div key={key++} style={lr.numberedRow}>
            <span style={lr.numberedBadge(color)}>{seg.label}</span>
            <p style={lr.stepText}>
              <RichInlineText text={seg.content} />
            </p>
          </div>
        );

      case 'bullet':
        return (
          <div key={key++} style={lr.bulletRow}>
            <span style={lr.bulletDot(color)} />
            <p style={lr.stepText}>
              <RichInlineText text={seg.content} />
            </p>
          </div>
        );

      case 'image-row':
        return (
          <LectureImageRow
            key={key++}
            images={seg.images.map((img) => ({
              ...img,
              imageUrl: img.url || img.imageUrl,
            }))}
          />
        );

      case 'table':
        return (
          <div key={key++} style={lr.tableWrap}>
            <table style={lr.table}>
              <tbody>
                {(seg.rows || []).map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => {
                      const Cell = rowIndex === 0 ? 'th' : 'td';
                      return (
                        <Cell key={cellIndex} style={rowIndex === 0 ? lr.tableHead : lr.tableCell}>
                          <RichInlineText text={cell} />
                        </Cell>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'paragraph':
      default:
        return (
          <p key={key++} style={lr.para}>
            <RichInlineText text={seg.content} />
          </p>
        );
    }
  });

  return (
    <>
      {els}
      {isStreaming && <span className="ged-cursor" style={{ color }} />}
    </>
  );
}

const lr = {
  sectionHeader: (color) => ({
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 17,
    fontWeight: 800,
    color: '#1b1b1d',
    borderLeft: `3px solid ${color}`,
    paddingLeft: 12,
    marginTop: 32,
    marginBottom: 10,
    letterSpacing: '-0.01em',
  }),
  headingSpeak: (active) => ({
    flexShrink: 0,
    width: 34,
    height: 34,
    borderRadius: 999,
    border: '1px solid #e2e8f0',
    background: active ? '#1b365d' : '#fff',
    color: active ? '#fff' : '#1b365d',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  }),
  mathBlock: {
    margin: '18px 0',
    padding: '16px 20px',
    background: '#f8fafc',
    borderRadius: 12,
    border: '1.5px solid #e2e8f0',
    overflowX: 'auto',
    textAlign: 'center',
  },
  tableWrap: { overflowX: 'auto', margin: '16px 0' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: 14, background: '#fff' },
  tableHead: {
    border: '1px solid #cbd5e1',
    padding: '8px 10px',
    textAlign: 'left',
    background: '#f8fafc',
    fontWeight: 700,
    color: '#1b1b1d',
  },
  tableCell: {
    border: '1px solid #cbd5e1',
    padding: '8px 10px',
    textAlign: 'left',
    color: '#334155',
    verticalAlign: 'top',
  },
  stepRow: {
    display: 'flex',
    gap: 12,
    margin: '10px 0',
    alignItems: 'flex-start',
  },
  stepBadge: (color) => ({
    background: color + '18',
    color,
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    marginTop: 2,
  }),
  stepText: {
    fontSize: 15,
    lineHeight: 1.75,
    color: '#334155',
    margin: 0,
  },
  numberedRow: {
    display: 'flex',
    gap: 12,
    margin: '6px 0',
    alignItems: 'flex-start',
  },
  numberedBadge: (color) => ({
    background: color + '10',
    color,
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 12,
    fontWeight: 800,
    flexShrink: 0,
    marginTop: 2,
    minWidth: 24,
    textAlign: 'center',
  }),
  bulletRow: {
    display: 'flex',
    gap: 10,
    margin: '6px 0',
    alignItems: 'flex-start',
  },
  bulletDot: (color) => ({
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
    marginTop: 8,
  }),
  para: {
    fontSize: 16,
    lineHeight: 1.85,
    color: '#334155',
    marginBottom: 0,
  },
  imageFigure: {
    margin: '18px 0',
    padding: 0,
  },
  imageRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 12,
    overflowX: 'auto',
    margin: '14px 0 22px',
  },
  imageCell: {
    flex: '1 1 0',
    minWidth: 0,
    margin: 0,
  },
  imageOpen: {
    display: 'block',
    width: '100%',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'zoom-in',
  },
  imageInfo: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    borderRadius: 999,
    border: 'none',
    background: 'rgba(255,255,255,0.94)',
    color: '#1b365d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(15,23,42,0.18)',
  },
  imageCount: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    minWidth: 28,
    height: 28,
    padding: '0 8px',
    borderRadius: 999,
    background: 'rgba(15,23,42,0.72)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imagePager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 8,
  },
  pageBtn: {
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#334155',
    borderRadius: 10,
    padding: '8px 12px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  pageLabel: { fontSize: 13, fontWeight: 700, color: '#64748b' },
  image: {
    width: '100%',
    maxHeight: 420,
    objectFit: 'contain',
    borderRadius: 12,
    border: '1.5px solid #e2e8f0',
    background: '#f8fafc',
    display: 'block',
  },
  imageInRow: {
    width: '100%',
    height: 200,
    objectFit: 'contain',
    borderRadius: 12,
    border: '1.5px solid #e2e8f0',
    background: '#f8fafc',
    display: 'block',
  },
  imageCap: {
    marginTop: 8,
    fontSize: 13,
    color: '#64748b',
    textAlign: 'center',
  },
};

/* ══════════════════════════════════════════
   Wikipedia Visual Reference
══════════════════════════════════════════ */
async function fetchWikiImage(query) {
  try {
    const url = `https://en.wikipedia.org/w/api.php?action=query&prop=pageimages&titles=${encodeURIComponent(query)}&pithumbsize=500&format=json&origin=*`;
    const res = await fetch(url);
    const data = await res.json();
    const pages = data?.query?.pages;
    if (!pages) return null;
    const page = Object.values(pages)[0];
    return page?.thumbnail?.source || null;
  } catch {
    return null;
  }
}

function WikiImages({ topic, lectureTitle }) {
  const [images, setImages] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setImages([]);
    setLoaded(false);
    const queries = [lectureTitle, topic];
    Promise.allSettled(queries.map((q) => fetchWikiImage(q))).then((results) => {
      const found = [
        ...new Set(
          results
            .filter((r) => r.status === 'fulfilled' && r.value)
            .map((r) => r.value)
        ),
      ].slice(0, 2);
      setImages(found);
      setLoaded(true);
    });
  }, [topic, lectureTitle]);

  if (!loaded || images.length === 0) return null;

  return (
    <div style={wi.section}>
      <div style={wi.header}>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#6332ed' }}>image_search</span>
        <div>
          <h3 style={wi.title}>Visual Reference</h3>
          <p style={wi.sub}>Educational images from Wikipedia</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {images.map((src, i) => (
          <a
            key={i}
            href={`https://en.wikipedia.org/wiki/${encodeURIComponent(i === 0 ? lectureTitle : topic)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={wi.imgWrap}
          >
            <img
              src={src}
              alt={i === 0 ? lectureTitle : topic}
              style={wi.img}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div style={wi.imgCaption}>
              <span className="material-symbols-outlined" style={{ fontSize: 12 }}>open_in_new</span>
              {i === 0 ? lectureTitle : topic} — Wikipedia
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

const wi = {
  section: { background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '20px 24px', marginBottom: 28 },
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  title: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 14, fontWeight: 800, color: '#1b1b1d', marginBottom: 2 },
  sub: { fontSize: 12, color: '#94a3b8' },
  imgWrap: { display: 'block', borderRadius: 12, overflow: 'hidden', border: '1.5px solid #e2e8f0', textDecoration: 'none', flex: '1 1 200px', maxWidth: 320 },
  img: { width: '100%', display: 'block', objectFit: 'contain', background: '#f8fafc', maxHeight: 220 },
  imgCaption: { display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', fontSize: 11, color: '#6332ed', fontWeight: 600, background: '#f8fafc', borderTop: '1px solid #e2e8f0' },
};

/* ══════════════════════════════════════════
   YouTube Section
══════════════════════════════════════════ */
function YouTubeSection({ videos }) {
  const list = (videos || []).filter((v) => extractYoutubeId(v.url) || toYoutubeEmbedUrl(v.url));
  if (!list.length) return null;

  return (
    <div style={yt.section}>
      <div style={yt.header}>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#ef4444' }}>smart_display</span>
        <div>
          <h3 style={yt.title}>Related YouTube Videos</h3>
          <p style={yt.sub}>Watch the videos selected for this lecture</p>
        </div>
      </div>
      <div style={yt.row}>
        {list.map((video) => {
          const id = extractYoutubeId(video.url);
          const thumb = id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : '';
          return (
            <a
              key={video.id || video.url}
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              style={yt.card}
            >
              <div style={yt.banner}>
                {thumb ? <img src={thumb} alt="" style={yt.bannerImg} /> : null}
                <span className="material-symbols-outlined" style={yt.playIcon}>play_circle</span>
              </div>
              <p style={yt.cardTitle}>{video.title || 'YouTube video'}</p>
            </a>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   Chat Box — voice input + per-message TTS
══════════════════════════════════════════ */
function ChatBox({ messages, streamingContent, streaming, input, setInput, loading, onSend, subject }) {
  const subjectData = SUBJECTS.find((s) => s.id === subject);
  const color = subjectData?.color || '#6332ed';
  const endRef = useRef(null);
  const [interimText, setInterimText] = useState('');

  const { supported: ttsSupported, speakingId, error: ttsError, speak, stop: stopSpeak } = useChatMessageTts();

  const onFinalTranscript = useCallback(
    (transcript) => {
      setInterimText('');
      setInput((prev) => {
        const trimmed = (prev || '').trim();
        return trimmed ? `${trimmed} ${transcript}` : transcript;
      });
    },
    [setInput]
  );

  const onInterimTranscript = useCallback((transcript) => {
    setInterimText(transcript);
  }, []);

  const {
    supported: micSupported,
    listening,
    error: micError,
    toggle: toggleMic,
    stop: stopMic,
  } = useSpeechRecognition({
    enabled: !loading && !streaming,
    onFinalTranscript,
    onInterimTranscript,
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, streaming, streamingContent]);

  // Stop mic / speech when lesson chat resets or reply starts streaming
  useEffect(() => {
    if (loading || streaming) {
      stopMic();
      setInterimText('');
    }
  }, [loading, streaming, stopMic]);

  useEffect(() => () => {
    stopMic();
    stopSpeak();
  }, [stopMic, stopSpeak]);

  const isBlocked = loading || streaming;
  const displayInput = interimText
    ? `${(input || '').trim() ? `${input.trim()} ` : ''}${interimText}`
    : input;

  const handleSend = () => {
    if (listening) stopMic();
    setInterimText('');
    stopSpeak();
    onSend();
  };

  return (
    <div style={chat.section}>
      <div style={chat.header}>
        <div style={{ ...chat.headerIcon, background: color + '18' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 20, color }}>school</span>
        </div>
        <div>
          <h3 style={chat.title}>Ask Your Teacher</h3>
          <p style={chat.sub}>
            Type or tap the mic to speak — tap the speaker on any message to hear it aloud.
          </p>
        </div>
      </div>

      <div style={chat.messages}>
        {messages.length === 0 && !loading && !streaming && (
          <div style={chat.empty}>
            <span className="material-symbols-outlined" style={{ fontSize: 30, color: '#cbd5e1', marginBottom: 8 }}>record_voice_over</span>
            <p style={{ fontSize: 13, color: '#94a3b8' }}>
              Ask by typing or voice — you&apos;ll get a thorough lesson-style answer.
            </p>
          </div>
        )}
        {messages.map((msg, i) => {
          const msgId = `${msg.role}-${i}`;
          const isSpeaking = speakingId === msgId;
          return (
            <div key={msgId} style={chat.row(msg.role === 'user')}>
              {msg.role === 'assistant' && (
                <div style={{ ...chat.avatar, background: color + '20', color }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>school</span>
                </div>
              )}
              <div style={chat.bubbleCol(msg.role === 'user')}>
                <div style={chat.bubble(msg.role === 'user', color)}>
                  {msg.role === 'assistant' ? (
                    <LectureRenderer content={msg.content} color={color} isStreaming={false} />
                  ) : (
                    msg.content
                  )}
                </div>
                {ttsSupported && msg.content?.trim() ? (
                  <button
                    type="button"
                    onClick={() => speak(msgId, msg.content)}
                    style={chat.speakBtn(msg.role === 'user', color, isSpeaking)}
                    title={isSpeaking ? 'Stop playback' : 'Play voice'}
                    aria-label={isSpeaking ? 'Stop voice playback' : 'Play message aloud'}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                      {isSpeaking ? 'stop_circle' : 'volume_up'}
                    </span>
                    {isSpeaking ? 'Stop' : 'Listen'}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}

        {loading && !streaming && (
          <div style={chat.row(false)}>
            <div style={{ ...chat.avatar, background: color + '20', color }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>school</span>
            </div>
            <div style={chat.typing}><span>●</span><span>●</span><span>●</span></div>
          </div>
        )}

        {streaming && (
          <div style={chat.row(false)}>
            <div style={{ ...chat.avatar, background: color + '20', color }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>school</span>
            </div>
            <div style={chat.bubbleCol(false)}>
              <div style={chat.bubble(false, color)}>
                <LectureRenderer content={streamingContent} color={color} isStreaming />
              </div>
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>

      {(micError || ttsError) && (
        <p style={chat.voiceError}>{micError || ttsError}</p>
      )}

      <div style={chat.inputRow}>
        {micSupported ? (
          <button
            type="button"
            onClick={toggleMic}
            disabled={isBlocked}
            style={chat.micBtn(color, listening, isBlocked)}
            title={listening ? 'Stop listening' : 'Speak your question'}
            aria-label={listening ? 'Stop voice input' : 'Start voice input'}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
              {listening ? 'mic' : 'mic_none'}
            </span>
          </button>
        ) : null}
        <input
          style={{
            ...chat.input,
            borderColor: listening ? color : '#e2e8f0',
            boxShadow: listening ? `0 0 0 2px ${color}22` : 'none',
          }}
          value={displayInput}
          onChange={(e) => {
            if (listening) stopMic();
            setInterimText('');
            setInput(e.target.value);
          }}
          placeholder={listening ? 'Listening… speak now' : 'Ask your teacher about this lesson…'}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && !isBlocked && handleSend()}
          disabled={isBlocked}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!input.trim() || isBlocked}
          style={{ ...chat.sendBtn, background: color, opacity: !input.trim() || isBlocked ? 0.5 : 1 }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span>
        </button>
      </div>
      {listening && (
        <p style={chat.listeningHint}>
          <span className="material-symbols-outlined" style={{ fontSize: 14, color }}>graphic_eq</span>
          Listening — speak your question, then tap send
        </p>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   Quiz Modal
══════════════════════════════════════════ */
function QuizModal({
  state,
  questions,
  answers,
  onAnswer,
  onSubmit,
  onProceed,
  onRetry,
  onContinueAnyway,
  onClose,
  subject,
  title = 'Lecture Quiz',
  subtitle = '',
  quizKind = 'lecture',
  isRevision = false,
  passMark = 4,
  proceedLabel = 'Continue',
  continueLabel = 'Move to Next Lecture',
  canSkip = false,
  canRetry = true,
  practiceOnly = false,
}) {
  const subjectData = SUBJECTS.find((s) => s.id === subject);
  const color = subjectData?.color || SBA.navy;
  const allAnswered = questions ? Object.keys(answers).length === questions.length : false;
  const score = questions ? questions.filter((q, i) => answers[i] === q.correct).length : 0;
  const passed = score >= passMark;
  const total = questions?.length || 0;

  return (
    <div style={qm.overlay}>
      <div style={qm.modal}>
        {state === 'loading' ? (
          <div style={qm.loading}>
            <div style={{ ...qm.loadingIcon, background: color + '18' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 36, color }}>quiz</span>
            </div>
            <h2 style={qm.loadingTitle}>
              {quizKind === 'module'
                ? 'Preparing your Module Exam…'
                : quizKind === 'image-map'
                  ? 'Opening your map-reading questions…'
                  : 'Preparing your lecture quiz…'}
            </h2>
            <p style={qm.loadingSub}>
              {quizKind === 'module'
                ? '25 questions from this module’s lecture quizzes (equal from each lecture), shuffled. Retry draws a new set. No AI.'
                : quizKind === 'image-map'
                  ? 'Using the MCQs saved with this image — no AI call'
                  : 'Opening the lecture quiz saved by your instructor — no AI call'}
            </p>
            <div style={qm.loadingBar}><div style={{ ...qm.loadingFill, background: color }} /></div>
          </div>
        ) : (
          <>
            <div style={{ ...qm.header, borderTop: `3px solid ${color}` }}>
              <div style={qm.headerLeft}>
                <span className="material-symbols-outlined" style={{ fontSize: 24, color }}>quiz</span>
                <div>
                  <h2 style={qm.headerTitle}>{title}</h2>
                  <p style={qm.headerSub}>{subtitle}</p>
                </div>
              </div>
              {state === 'submitted' && (
                <div style={qm.scoreBadge(passed ? '#10b981' : '#f59e0b')}>
                  {score}/{total} Correct
                </div>
              )}
              {state === 'active' && (
                <button type="button" onClick={onClose} style={qm.closeBtn} aria-label="Close quiz">
                  <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
                </button>
              )}
            </div>

            <div style={qm.body}>
              {questions?.map((q, qIdx) => {
                const isSubmitted = state === 'submitted';
                const selected = answers[qIdx];
                const correctIdx = q.correct;
                return (
                  <div key={qIdx} style={qm.question}>
                    <p style={qm.questionText}>
                      <span style={{ ...qm.qNum, background: color + '18', color }}>{qIdx + 1}</span>
                      {q.question}
                    </p>
                    {q.questionImageUrl ? (
                      <figure style={{ margin: '0 0 12px 40px' }}>
                        <SafeImage
                          src={q.questionImageUrl}
                          alt={q.questionImageDescription || ''}
                          style={{
                            maxWidth: '100%',
                            maxHeight: 240,
                            objectFit: 'contain',
                            borderRadius: 12,
                            border: '1.5px solid #e2e8f0',
                            background: '#f8fafc',
                            display: 'block',
                          }}
                        />
                        {q.questionImageDescription ? (
                          <figcaption style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                            {q.questionImageDescription}
                          </figcaption>
                        ) : null}
                      </figure>
                    ) : null}
                    <div style={qm.options}>
                      {(q.options || []).map((opt, optIdx) => {
                        const isSelected = selected === optIdx;
                        const isCorrectOpt = optIdx === correctIdx;
                        const showFeedback = isSubmitted && (isSelected || isCorrectOpt);
                        const imgUrl = optionImageUrl(opt);
                        const label = optionText(opt);

                        let bg = '#f8fafc';
                        let border = '#e2e8f0';
                        let textColor = '#334155';
                        if (!isSubmitted && isSelected) {
                          bg = color + '12';
                          border = color;
                          textColor = '#1b1b1d';
                        }
                        if (isSubmitted && isCorrectOpt) {
                          bg = '#f0fdf4';
                          border = '#16a34a';
                          textColor = '#15803d';
                        }
                        if (isSubmitted && isSelected && !isCorrectOpt) {
                          bg = '#fef2f2';
                          border = '#ef4444';
                          textColor = '#b91c1c';
                        }

                        return (
                          <div key={optIdx}>
                            <button
                              type="button"
                              onClick={() => !isSubmitted && onAnswer(qIdx, optIdx)}
                              disabled={isSubmitted}
                              style={{
                                ...qm.option,
                                background: bg,
                                borderColor: border,
                                color: textColor,
                                cursor: isSubmitted ? 'default' : 'pointer',
                                alignItems: imgUrl ? 'flex-start' : 'center',
                              }}
                            >
                              <span
                                style={{
                                  ...qm.optLetter,
                                  background: isSelected || (isSubmitted && isCorrectOpt) ? color : '#e2e8f0',
                                  color: isSelected || (isSubmitted && isCorrectOpt) ? '#fff' : '#64748b',
                                }}
                              >
                                {String.fromCharCode(65 + optIdx)}
                              </span>
                              <span style={{ flex: 1, textAlign: 'left' }}>
                                {label ? <span style={{ display: 'block' }}>{label}</span> : null}
                                {imgUrl ? (
                                  <SafeImage
                                    src={imgUrl}
                                    alt={label}
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: 140,
                                      objectFit: 'contain',
                                      borderRadius: 8,
                                      marginTop: label ? 8 : 0,
                                      display: 'block',
                                    }}
                                  />
                                ) : null}
                              </span>
                              {isSubmitted && isCorrectOpt && (
                                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#16a34a' }}>
                                  check_circle
                                </span>
                              )}
                              {isSubmitted && isSelected && !isCorrectOpt && (
                                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#ef4444' }}>
                                  cancel
                                </span>
                              )}
                            </button>
                            {showFeedback && (
                              <div style={qm.explanation(isCorrectOpt)}>
                                <span className="material-symbols-outlined" style={{ fontSize: 14, flexShrink: 0 }}>
                                  {isCorrectOpt ? 'lightbulb' : 'info'}
                                </span>
                                <span>
                                  {!isCorrectOpt && isSelected
                                    ? `Incorrect. Correct answer: ${String.fromCharCode(65 + correctIdx)}. `
                                    : ''}
                                  {q.explanations?.[optIdx] ||
                                    (isCorrectOpt ? 'This is the correct answer.' : 'This is incorrect.')}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {isSubmitted && selected !== correctIdx && (
                      <p style={qm.correctReveal}>
                        Correct answer:{' '}
                        <strong>
                          {String.fromCharCode(65 + correctIdx)}. {optionText((q.options || [])[correctIdx])}
                        </strong>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={qm.footer}>
              {state === 'active' ? (
                <div style={qm.footerRow}>
                  <p style={qm.footerHint}>
                    {Object.keys(answers).length}/{total} answered · Need {passMark}/{total} to pass
                  </p>
                  <button
                    type="button"
                    onClick={onSubmit}
                    disabled={!allAnswered}
                    style={{ ...qm.submitBtn, background: color, opacity: allAnswered ? 1 : 0.4 }}
                  >
                    Submit Answers
                  </button>
                </div>
              ) : (
                <div style={qm.footerCol}>
                  <div style={qm.resultMsg(passed)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                      {passed ? 'celebration' : 'info'}
                    </span>
                    {passed
                      ? `Passed! ${score}/${total} correct.`
                      : practiceOnly
                        ? `Score ${score}/${total}. You can retry this saved exam anytime.`
                        : canSkip && quizKind === 'module'
                        ? `Not passed — ${score}/${total} after two tries. You can retry the Module Exam or move to the next module.`
                        : canSkip && quizKind === 'image-map'
                        ? `Not passed — ${score}/${total} after two tries. You can retry or continue to the next reading.`
                        : canSkip
                        ? `Not passed — ${score}/${total} after two tries. You cannot retry this quiz. Continue to the next lecture.`
                        : `Not passed — ${score}/${total}. Need ${passMark}+. You have one retry.`}
                  </div>
                  {passed ? (
                    <button type="button" onClick={onProceed} style={{ ...qm.proceedBtn, background: color }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_forward</span>
                      {practiceOnly ? 'Close' : proceedLabel}
                    </button>
                  ) : (
                    <div style={qm.failActions}>
                      {canRetry && !practiceOnly ? (
                        <button type="button" onClick={onRetry} style={{ ...qm.retryBtnOutline, borderColor: color, color }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 17 }}>refresh</span>
                          {quizKind === 'module' ? 'Retry Module Exam' : 'Try quiz again'}
                        </button>
                      ) : null}
                      {canSkip && !practiceOnly ? (
                        <button type="button" onClick={onContinueAnyway} style={{ ...qm.proceedBtn, background: color }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_forward</span>
                          {continueLabel}
                        </button>
                      ) : null}
                      {practiceOnly ? (
                        <button type="button" onClick={onClose} style={{ ...qm.proceedBtn, background: color }}>
                          Close
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   Welcome Screen
══════════════════════════════════════════ */
function WelcomeScreen({ modules, daysByModule, completedLessons, onSelectSubject }) {
  const totalLessons = Object.values(daysByModule).reduce((s, d) => s + (d?.length || 0), 0);
  const completedCount = Object.keys(completedLessons).length;
  return (
    <div style={s.welcome}>
      <div style={s.welcomeIcon}>
        <span className="material-symbols-outlined" style={{ fontSize: 44, color: SBA.navy }}>
          auto_stories
        </span>
      </div>
      <h2 style={s.welcomeTitle}>GED Prep Program</h2>
      <p style={s.welcomeSub}>
        {modules.length > 0
          ? `${totalLessons} lectures across 4 subjects — pick one subject and study one module at a time.`
          : 'Course content is being prepared. Check back soon!'}
      </p>
      {completedCount > 0 && (
        <div style={s.progressSummary}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#10b981' }}>
            check_circle
          </span>
          {completedCount} of {totalLessons} lectures completed
        </div>
      )}
      {SUBJECTS.map((subject) => {
        const subjectMods = modules.filter((m) => m.subject === subject.id);
        if (subjectMods.length === 0) return null;
        const total = subjectMods.reduce((sum, m) => sum + (daysByModule[m.id]?.length || 0), 0);
        const done = subjectMods.reduce(
          (sum, m) => sum + (daysByModule[m.id] || []).filter((d) => completedLessons[d.id]).length,
          0
        );
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        return (
          <button
            key={subject.id}
            type="button"
            onClick={() => onSelectSubject?.(subject.id)}
            style={{ ...s.subjectCard(subject.color), cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
          >
            <div style={s.subjectCardLeft}>
              <div style={{ ...s.subjectCardIcon, background: subject.color + '18' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 22, color: subject.color }}>
                  {subject.icon}
                </span>
              </div>
              <div>
                <p style={s.subjectCardTitle}>{subject.label}</p>
                <p style={s.subjectCardMeta}>
                  {subjectMods.length} module{subjectMods.length !== 1 ? 's' : ''} · {total} lecture
                  {total !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div style={s.subjectCardRight}>
              <div style={s.progressTrack}>
                <div style={{ ...s.progressFill, width: `${pct}%`, background: subject.color }} />
              </div>
              <p style={{ ...s.progressPct, color: subject.color }}>{pct}%</p>
            </div>
          </button>
        );
      })}
      <p style={s.startHint}>Choose a subject above, then open a lecture from the list</p>
    </div>
  );
}

/* ══════════════════════════════════════════
   Styles
══════════════════════════════════════════ */
const s = {
  contentWrap: { maxWidth: 800, margin: '0 auto', width: '100%', padding: '8px 8px 80px', flex: 1, boxSizing: 'border-box' },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, flexWrap: 'wrap' },
  breadcrumbSubject: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700 },
  breadcrumbArrow: { fontSize: 14, color: '#cbd5e1' },
  breadcrumbModule: { fontSize: 12, color: '#64748b', fontWeight: 500 },
  breadcrumbDay: { fontSize: 12, color: '#94a3b8', fontWeight: 500 },
  lessonHeader: { marginBottom: 20 },
  lessonPillRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  subjectPill: { display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  aiPill: { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg, rgba(27,54,93,0.08), #f8fafc)', color: SBA.navy, border: '1px solid #cbd5e1', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  regenBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid #e2e8f0', borderRadius: 20, color: '#64748b', padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  lessonTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 30, fontWeight: 800, color: '#1b1b1d', letterSpacing: '-0.025em', lineHeight: 1.2, marginBottom: 8 },
  lessonSubtitle: { fontSize: 13, color: '#94a3b8', fontWeight: 500 },
  contentDivider: { height: 1, background: '#e2e8f0', margin: '20px 0 28px' },
  solvedImageSection: {
    marginBottom: 28,
    padding: 20,
    background: '#f8fafc',
    borderRadius: 16,
    border: '1.5px solid #e2e8f0',
  },
  solvedImageHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  solvedImageTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 16, fontWeight: 800, color: '#1b1b1d', margin: 0 },
  solvedImage: { width: '100%', maxHeight: 420, objectFit: 'contain', borderRadius: 12, border: '1px solid #e2e8f0', background: '#fff' },
  solvedImageCaption: { marginTop: 12, fontSize: 13, color: '#64748b', lineHeight: 1.6 },
  aiLoadingBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '48px 24px', marginBottom: 32, background: 'linear-gradient(135deg, #fafafa, #f1f5f9)', borderRadius: 20, border: '1.5px solid rgba(27,54,93,0.12)' },
  aiLoadingIconWrap: { width: 64, height: 64, borderRadius: 18, background: 'linear-gradient(135deg, rgba(27,54,93,0.08), #f8fafc)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 },
  aiLoadingTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 800, color: '#1b1b1d', marginBottom: 8 },
  aiLoadingSub: { fontSize: 14, color: '#64748b', lineHeight: 1.6, maxWidth: 360, marginBottom: 24 },
  aiLoadingBar: { width: 200, height: 4, background: '#e2e8f0', borderRadius: 9999, overflow: 'hidden' },
  aiLoadingFill: { height: '100%', width: '65%', borderRadius: 9999 },
  aiErrorBox: { display: 'flex', alignItems: 'flex-start', gap: 14, background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 14, padding: '20px 24px', marginBottom: 32 },
  aiErrorTitle: { fontSize: 14, fontWeight: 700, color: '#991b1b', marginBottom: 4 },
  aiErrorMsg: { fontSize: 13, color: '#b91c1c', lineHeight: 1.5 },
  retryBtn: { display: 'flex', alignItems: 'center', gap: 5, background: '#fff', border: '1.5px solid #fecaca', borderRadius: 8, color: '#ef4444', padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 },
  lectureBody: { marginBottom: 36 },
  lecturePara: { fontSize: 16, lineHeight: 1.85, color: '#334155', marginBottom: 0 },
  noContent: { fontSize: 15, color: '#94a3b8', fontStyle: 'italic' },
  completionBar: { padding: '24px 0', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', marginBottom: 28, display: 'flex', alignItems: 'center' },
  markBtn: { display: 'flex', alignItems: 'center', gap: 8, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' },
  doneBadge: { display: 'flex', alignItems: 'center', gap: 8, background: '#f0fdf4', color: '#16a34a', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 700, border: '1.5px solid #bbf7d0' },
  navRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 32 },
  navBtn: (isPrimary) => ({ display: 'flex', alignItems: 'center', gap: 10, background: isPrimary ? SBA.navy : '#fff', color: isPrimary ? '#fff' : '#1b1b1d', border: isPrimary ? 'none' : '1.5px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', cursor: 'pointer', flex: 1, maxWidth: '45%', justifyContent: isPrimary ? 'flex-end' : 'flex-start', boxShadow: isPrimary ? '0 4px 14px rgba(15,23,42,0.15)' : 'none' }),
  navBtnLabel: { fontSize: 10, fontWeight: 600, opacity: 0.5, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 3 },
  navBtnTitle: { fontSize: 13, fontWeight: 700, lineHeight: 1.3 },
  nextLockTip: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8', fontWeight: 500 },
  courseCompleteTip: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#f59e0b', fontWeight: 600 },
  welcome: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px 48px', maxWidth: 680, margin: '0 auto', width: '100%' },
  welcomeIcon: { width: 80, height: 80, borderRadius: 22, background: 'rgba(27,54,93,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  welcomeTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 26, fontWeight: 800, color: '#1b1b1d', letterSpacing: '-0.025em', marginBottom: 10 },
  welcomeSub: { fontSize: 14, color: '#64748b', textAlign: 'center', maxWidth: 400, lineHeight: 1.6, marginBottom: 16 },
  progressSummary: { display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', color: '#16a34a', borderRadius: 20, padding: '7px 16px', fontSize: 13, fontWeight: 600, marginBottom: 28, border: '1px solid #bbf7d0' },
  subjectCard: (color) => ({ width: '100%', background: '#fff', border: `1.5px solid ${color}30`, borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 10 }),
  subjectCardLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  subjectCardIcon: { width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  subjectCardTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 14, fontWeight: 800, color: '#1b1b1d', marginBottom: 3 },
  subjectCardMeta: { fontSize: 12, color: '#94a3b8', fontWeight: 500 },
  subjectCardRight: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 120 },
  progressTrack: { flex: 1, height: 6, background: '#e2e8f0', borderRadius: 9999, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 9999, transition: 'width 0.4s' },
  progressPct: { fontSize: 12, fontWeight: 700, flexShrink: 0, minWidth: 32, textAlign: 'right' },
  startHint: { fontSize: 13, color: '#94a3b8', marginTop: 24, fontStyle: 'italic' },
};

/* YouTube styles */
const yt = {
  section: { background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '18px 20px 20px', marginBottom: 28 },
  header: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 },
  title: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 15, fontWeight: 800, color: '#1b1b1d', marginBottom: 2 },
  sub: { fontSize: 12, color: '#94a3b8' },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  card: {
    flex: '0 0 188px',
    width: 188,
    textDecoration: 'none',
    background: '#f8fafc',
    border: '1.5px solid #e2e8f0',
    borderRadius: 12,
    overflow: 'hidden',
    display: 'block',
  },
  banner: {
    height: 96,
    position: 'relative',
    background: '#0f172a',
    overflow: 'hidden',
  },
  bannerImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  playIcon: {
    position: 'absolute',
    inset: 0,
    margin: 'auto',
    fontSize: 34,
    color: '#fff',
    width: 34,
    height: 34,
    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.45))',
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#1b1b1d',
    lineHeight: 1.35,
    margin: 0,
    padding: '8px 10px 10px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
};

/* Chat styles */
const chat = {
  section: { background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, overflow: 'hidden', marginBottom: 28 },
  header: { display: 'flex', alignItems: 'center', gap: 14, padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9' },
  headerIcon: { width: 40, height: 40, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 15, fontWeight: 800, color: '#1b1b1d', marginBottom: 2 },
  sub: { fontSize: 12, color: '#94a3b8' },
  messages: { minHeight: 120, maxHeight: 480, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '24px 0' },
  row: (isUser) => ({ display: 'flex', alignItems: 'flex-end', gap: 8, justifyContent: isUser ? 'flex-end' : 'flex-start' }),
  avatar: { width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  bubbleCol: (isUser) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: isUser ? 'flex-end' : 'flex-start',
    gap: 6,
    maxWidth: isUser ? '78%' : '92%',
  }),
  bubble: (isUser, color) => ({
    maxWidth: '100%', padding: isUser ? '10px 14px' : '14px 18px', borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    background: isUser ? color : '#f8fafc',
    color: isUser ? '#fff' : '#334155',
    fontSize: 13, lineHeight: 1.6, fontWeight: 400,
    border: isUser ? 'none' : '1px solid #e2e8f0',
  }),
  speakBtn: (isUser, color, active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: active ? `${color}18` : '#fff',
    border: `1px solid ${active ? color : '#e2e8f0'}`,
    color: active ? color : '#64748b',
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
  }),
  typing: { background: '#f6f3f5', borderRadius: '16px 16px 16px 4px', padding: '12px 16px', display: 'flex', gap: 4, alignItems: 'center' },
  inputRow: { display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #f1f5f9', background: '#fafafa', alignItems: 'center' },
  input: { flex: 1, padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 13, outline: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif', color: '#1b1b1d', background: '#fff' },
  sendBtn: { width: 40, height: 40, borderRadius: 10, border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 },
  micBtn: (color, listening, disabled) => ({
    width: 40,
    height: 40,
    borderRadius: 10,
    border: `1.5px solid ${listening ? color : '#e2e8f0'}`,
    background: listening ? `${color}18` : '#fff',
    color: listening ? color : '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    flexShrink: 0,
    opacity: disabled ? 0.5 : 1,
    boxShadow: listening ? `0 0 0 3px ${color}22` : 'none',
  }),
  listeningHint: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    margin: '0 16px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#64748b',
  },
  voiceError: {
    margin: '0 16px 8px',
    fontSize: 12,
    color: '#b91c1c',
    fontWeight: 500,
  },
};

/* Quiz modal styles */
const qm = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '24px 20px', overflowY: 'auto' },
  modal: { background: '#fff', borderRadius: 20, width: '100%', maxWidth: 720, boxShadow: '0 25px 60px rgba(0,0,0,0.25)', overflow: 'hidden', marginBottom: 24 },
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 40px', textAlign: 'center' },
  loadingIcon: { width: 72, height: 72, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  loadingTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 20, fontWeight: 800, color: '#1b1b1d', marginBottom: 10 },
  loadingSub: { fontSize: 14, color: '#64748b', marginBottom: 28 },
  loadingBar: { width: 200, height: 4, background: '#e2e8f0', borderRadius: 9999, overflow: 'hidden' },
  loadingFill: { height: '100%', width: '60%', borderRadius: 9999 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px 16px', borderBottom: '1px solid #f1f5f9', gap: 12 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  headerTitle: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 800, color: '#1b1b1d' },
  headerSub: { fontSize: 12, color: '#64748b', marginTop: 2, maxWidth: 420, lineHeight: 1.4 },
  closeBtn: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    width: 36,
    height: 36,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#64748b',
    flexShrink: 0,
  },
  scoreBadge: (color) => ({ background: color + '15', color, border: `1.5px solid ${color}40`, borderRadius: 20, padding: '6px 16px', fontSize: 14, fontWeight: 800 }),
  body: { padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 32, maxHeight: '60vh', overflowY: 'auto' },
  question: {},
  questionText: { fontSize: 15, fontWeight: 600, color: '#1b1b1d', lineHeight: 1.5, marginBottom: 14, display: 'flex', alignItems: 'flex-start', gap: 10 },
  qNum: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', fontSize: 12, fontWeight: 800, flexShrink: 0, marginTop: 1 },
  options: { display: 'flex', flexDirection: 'column', gap: 8 },
  option: { width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1.5px solid', textAlign: 'left', fontSize: 14, fontWeight: 500, lineHeight: 1.4, transition: 'all 0.15s', fontFamily: 'Plus Jakarta Sans, sans-serif' },
  optLetter: { width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 },
  explanation: (isCorrect) => ({ display: 'flex', alignItems: 'flex-start', gap: 8, background: isCorrect ? '#f0fdf4' : '#fef2f2', color: isCorrect ? '#15803d' : '#b91c1c', borderRadius: 8, padding: '10px 12px', marginTop: 6, fontSize: 12, lineHeight: 1.5 }),
  correctReveal: {
    marginTop: 10,
    marginBottom: 0,
    fontSize: 13,
    color: '#15803d',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 10,
    padding: '10px 12px',
    lineHeight: 1.45,
  },
  footer: { padding: '18px 28px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' },
  footerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' },
  footerCol: { display: 'flex', flexDirection: 'column', gap: 14, width: '100%' },
  footerHint: { fontSize: 13, color: '#94a3b8', fontWeight: 500 },
  submitBtn: { color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  resultMsg: (passed) => ({ display: 'flex', alignItems: 'center', gap: 8, background: passed ? '#f0fdf4' : '#fffbeb', color: passed ? '#15803d' : '#b45309', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, width: '100%' }),
  failActions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', width: '100%' },
  retryBtnOutline: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#fff',
    border: '1.5px solid',
    borderRadius: 10,
    padding: '12px 18px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
  },
  proceedBtn: { display: 'flex', alignItems: 'center', gap: 8, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },
};
